import axios from 'axios';
import { offlineQueue } from './offlineQueue';
import * as apiCache from './apiCache';

// Resolve API base URL at runtime — self-heals when a stale Emergent preview
// URL was baked into the bundle but the app now runs on a different domain
// (e.g. production at app.stockandload.com).
const BACKEND_URL = (() => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (!envUrl) return window.location.origin;

  try {
    const envHost = new URL(envUrl).hostname;
    const currentHost = window.location.hostname;

    // Exact match — configured URL matches where we're running
    if (envHost === currentHost) return envUrl;

    // Running on production (non-Emergent, non-localhost) but env points to
    // an Emergent preview host → use current origin instead
    const isEmergentUrl = envHost.includes('.emergent.');
    const isLocalDev = currentHost === 'localhost' || currentHost === '127.0.0.1';

    if (!isLocalDev && isEmergentUrl) {
      console.warn(
        `[API] REACT_APP_BACKEND_URL points to ${envHost} but app is running on ${currentHost}. ` +
        `Using ${window.location.origin} instead.`
      );
      return window.location.origin;
    }
  } catch {
    // URL parsing failed — fall through to use envUrl as-is
  }

  return envUrl;
})();
const API_BASE = `${BACKEND_URL}/api`;

// =============================================================================
// DEBUG LOGGING FOR INVENTORY LOADING ISSUES
// Enable with localStorage.setItem('DEBUG_API', 'true')
// =============================================================================
const isDebugEnabled = () => localStorage.getItem('DEBUG_API') === 'true';

const logApiRequest = (config) => {
  if (!isDebugEnabled()) return;
  console.log(`[API_REQ] ${config.method?.toUpperCase()} ${config.url}`, {
    params: config.params,
    hasAuth: !!config.headers?.Authorization,
    timestamp: new Date().toISOString()
  });
};

const logApiResponse = (response, duration) => {
  if (!isDebugEnabled()) return;
  console.log(`[API_RES] ${response.status} ${response.config?.url} (${duration}ms)`, {
    dataType: typeof response.data,
    dataKeys: response.data ? Object.keys(response.data) : null
  });
};

const logApiError = (error, duration) => {
  // Suppress logging for polling requests when polling is paused
  if (pollingPaused && error.code === 'ECONNABORTED') return;
  
  const details = {
    url: error.config?.url || 'unknown',
    method: error.config?.method?.toUpperCase() || 'unknown',
    status: error.response?.status || 'NO_RESPONSE',
    statusText: error.response?.statusText || error.message,
    errorCode: error.code,
    duration: duration ? `${duration}ms` : 'unknown',
    isTimeout: error.code === 'ECONNABORTED',
    isNetworkError: !error.response && error.message === 'Network Error',
    responseData: error.response?.data,
    timestamp: new Date().toISOString()
  };
  
  console.error(`[API_ERR] ${details.method} ${details.url} → ${details.status}`, details);
  
  // Store last 10 API errors for debugging
  try {
    const errors = JSON.parse(localStorage.getItem('API_ERRORS') || '[]');
    errors.unshift(details);
    localStorage.setItem('API_ERRORS', JSON.stringify(errors.slice(0, 10)));
  } catch (e) {
    // Ignore storage errors
  }
};

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// =============================================================================
// LEGAL ACCEPTANCE RACE CONDITION GUARD
// =============================================================================
// After accepting legal terms, stale in-flight requests (sent with the old token)
// may still return 403 LEGAL_REQUIRED. This flag prevents the interceptor from
// doing a hard redirect during the window after acceptance.
let legalAcceptedTimestamp = 0;
const LEGAL_SUPPRESS_WINDOW_MS = 10000; // 10 seconds

export const markLegalJustAccepted = () => {
  legalAcceptedTimestamp = Date.now();
};

// =============================================================================
// P0 FIX: POLLING PAUSE SYSTEM
// =============================================================================
// When critical operations (like receive-items) are in progress, pause all
// background polling to prevent connection saturation (browser 6-connection limit)

let pollingPaused = false;
let pollingPauseReason = null;

// Pause polling - called when PartialReceiveModal opens or submit starts
export const pausePolling = (reason = 'unknown') => {
  pollingPaused = true;
  pollingPauseReason = reason;
  console.log('[API_POLLING] Paused:', reason);
};

// Resume polling - called when modal closes or submit completes
export const resumePolling = () => {
  pollingPaused = false;
  pollingPauseReason = null;
  console.log('[API_POLLING] Resumed');
};

// Check if polling is paused
export const isPollingPaused = () => pollingPaused;

// =============================================================================
// P0 DEBUG: REQUEST ID TRACKING
// =============================================================================

// Track in-flight requests for debugging
let inFlightRequestCount = 0;

// Generate UUID v4
const generateRequestId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Get debug info for request logging
const getRequestDebugInfo = () => {
  return {
    serviceWorkerActive: typeof navigator !== 'undefined' && 
                         'serviceWorker' in navigator && 
                         !!navigator.serviceWorker.controller,
    inFlightRequests: inFlightRequestCount,
    pollingPaused: pollingPaused,
    timestamp: new Date().toISOString()
  };
};

// =============================================================================
// WAREHOUSE CONTEXT INTEGRATION
// =============================================================================

// Global warehouse ID getter - will be set by WarehouseContext
let getSelectedWarehouseId = () => null;

// Global warehouse fallback handler - will be set by WarehouseContext
let warehouseFallbackHandler = null;

// Track pending retries to prevent infinite loops
let pendingRetryRequests = new Set();

// Function to set the warehouse ID getter (called by WarehouseContext)
export const setWarehouseIdGetter = (getter) => {
  getSelectedWarehouseId = getter;
};

// Function to set the warehouse fallback handler (called by WarehouseContext)
export const setWarehouseFallbackHandler = (handler) => {
  warehouseFallbackHandler = handler;
};

// List of endpoint patterns that should include warehouse_id
// These are endpoints affected by Phase 3a multi-warehouse refactor
const WAREHOUSE_AFFECTED_ENDPOINTS = [
  // warehouse.py endpoints
  { method: 'GET', pattern: /^\/warehouse$/ },
  { method: 'PUT', pattern: /^\/warehouse$/ },
  { method: 'GET', pattern: /^\/warehouse\/inventory$/ },
  { method: 'POST', pattern: /^\/warehouse\/inventory\/restock$/ },
  { method: 'POST', pattern: /^\/warehouse\/inventory\/use$/ },
  { method: 'POST', pattern: /^\/warehouse\/inventory\/bulk-restock$/ },
  { method: 'PUT', pattern: /^\/warehouse\/inventory\/[^/]+$/ },
  { method: 'POST', pattern: /^\/warehouse\/inventory\/bulk-threshold$/ },
  { method: 'POST', pattern: /^\/warehouse\/thresholds\/import\/preview$/ },
  { method: 'POST', pattern: /^\/warehouse\/thresholds\/import\/apply$/ },
  { method: 'GET', pattern: /^\/warehouse\/export$/ },
  // Transfer endpoint uses from_warehouse_id and to_warehouse_id separately
  // barcode.py endpoints
  { method: 'GET', pattern: /^\/scan\/lookup/ },
  // server.py endpoints
  { method: 'GET', pattern: /^\/reorder$/ },
  { method: 'GET', pattern: /^\/search$/ },
  // Warehouse import endpoints
  { method: 'POST', pattern: /^\/warehouse\/validate-import/ },
  { method: 'POST', pattern: /^\/warehouse\/execute-import/ },
];

// Check if an endpoint should include warehouse_id
const shouldIncludeWarehouseId = (method, url) => {
  const upperMethod = method?.toUpperCase();
  return WAREHOUSE_AFFECTED_ENDPOINTS.some(
    ep => ep.method === upperMethod && ep.pattern.test(url)
  );
};

// Helper to inject warehouse_id into request config
const injectWarehouseId = (config) => {
  const warehouseId = getSelectedWarehouseId();
  
  // Only inject if we have a warehouse ID and the endpoint needs it
  if (!warehouseId || !shouldIncludeWarehouseId(config.method, config.url)) {
    return config;
  }
  
  // For GET requests, add to params
  if (config.method?.toLowerCase() === 'get') {
    config.params = config.params || {};
    if (!config.params.warehouse_id) {
      config.params.warehouse_id = warehouseId;
    }
  } else {
    // For POST/PUT/DELETE, add as query param to URL
    const separator = config.url.includes('?') ? '&' : '?';
    if (!config.url.includes('warehouse_id=')) {
      config.url = `${config.url}${separator}warehouse_id=${warehouseId}`;
    }
  }
  
  return config;
};

// =============================================================================
// REQUEST INTERCEPTORS
// =============================================================================

api.interceptors.request.use(
  (config) => {
    // Track request start time for duration measurement
    config.metadata = { startTime: Date.now() };
    
    // P0 DEBUG: Generate and attach request ID
    const requestId = generateRequestId();
    config.headers['X-Client-Request-Id'] = requestId;
    config.metadata.requestId = requestId;
    
    // P0 DEBUG: Track in-flight requests
    inFlightRequestCount++;
    
    // P0 DEBUG: Log detailed info for receive-items requests
    if (config.url && config.url.includes('/receive-items')) {
      const debugInfo = getRequestDebugInfo();
      console.log('[API_DEBUG] receive-items request starting:', {
        requestId: requestId,
        url: config.url,
        fullUrl: `${API_BASE}${config.url}`,
        startTimestamp: debugInfo.timestamp,
        serviceWorkerActive: debugInfo.serviceWorkerActive,
        inFlightRequests: debugInfo.inFlightRequests,
        pollingPaused: debugInfo.pollingPaused,
        payloadSize: JSON.stringify(config.data || {}).length,
        headers: Object.keys(config.headers || {}).join(', ')
      });
      
      // P0 DEBUG: Test if we can connect at all
      console.log('[API_DEBUG] Testing connection...', {
        online: navigator.onLine,
        connectionType: navigator.connection?.effectiveType || 'unknown',
        downlink: navigator.connection?.downlink || 'unknown'
      });
    }
    
    // Add auth token
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Inject warehouse_id for affected endpoints
    config = injectWarehouseId(config);
    
    // Log request (if debug enabled)
    logApiRequest(config);
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Cache layer: serve cached GET responses when fresh
api.interceptors.request.use(apiCache.requestInterceptor);
// Writes that never change business data shown on screens (telemetry, prefs, auth, read-receipts)
const SILENT_MUTATIONS = ['/auth/', '/storage/session', '/tenant/timezone', '/telemetry', '/perf', '/client-logs', '/analytics', '/notifications/mark', '/mark-read', '/heartbeat', '/tour-state', '/user-preferences', '/settings/inventory-display/me'];
// Cache layer: store successful GET responses + invalidate on mutations
api.interceptors.response.use((response) => {
  const resp = apiCache.responseInterceptor(response);
  // Invalidate related caches on successful mutations
  const method = response.config?.method;
  if (method && method !== 'get') {
    const url = response.config?.url || '';
    if (url.includes('/warehouse')) apiCache.invalidate('/warehouse');
    if (url.includes('/purchase-order')) apiCache.invalidate('/purchase-orders');
    if (url.includes('/item')) apiCache.invalidate('/items');
    if (url.includes('/van') || url.includes('/vehicle')) apiCache.invalidate('/vans');
    if (url.includes('/vendor')) apiCache.invalidate('/vendors');
    if (url.includes('/onboarding')) apiCache.invalidate('/onboarding');
    if (!SILENT_MUTATIONS.some((p) => url.includes(p))) apiCache.onMutationSuccess(response.config);
  }
  return resp;
});

api.interceptors.response.use(
  (response) => {
    // Calculate duration
    const duration = response.config?.metadata?.startTime 
      ? Date.now() - response.config.metadata.startTime 
      : null;
    
    // P0 DEBUG: Decrement in-flight counter
    inFlightRequestCount = Math.max(0, inFlightRequestCount - 1);
    
    // P0 DEBUG: Log detailed info for receive-items responses
    if (response.config?.url && response.config.url.includes('/receive-items')) {
      const requestId = response.config.metadata?.requestId || 'unknown';
      const serverRequestId = response.headers?.['x-request-id'] || 'none';
      console.log('[API_DEBUG] receive-items response:', {
        clientRequestId: requestId,
        serverRequestId: serverRequestId,
        status: response.status,
        duration: duration,
        endTimestamp: new Date().toISOString()
      });
    }
    
    // Log successful response (if debug enabled)
    logApiResponse(response, duration);
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Calculate duration
    const duration = originalRequest?.metadata?.startTime 
      ? Date.now() - originalRequest.metadata.startTime 
      : null;
    
    // P0 DEBUG: Decrement in-flight counter
    inFlightRequestCount = Math.max(0, inFlightRequestCount - 1);
    
    // P0 DEBUG: Log detailed error info for receive-items
    if (originalRequest?.url && originalRequest.url.includes('/receive-items')) {
      const requestId = originalRequest.metadata?.requestId || 'unknown';
      
      // Gather diagnostic info
      const diagnosticInfo = {
        clientRequestId: requestId,
        errorCode: error.code,
        errorMessage: error.message,
        status: error.response?.status,
        duration: duration,
        endTimestamp: new Date().toISOString(),
        isTimeout: error.code === 'ECONNABORTED',
        isNetworkError: !error.response,
        
        // Additional diagnostics
        online: navigator.onLine,
        connectionType: navigator.connection?.effectiveType || 'unknown',
        
        // Check if the request was actually sent
        wasSent: !!error.request,
        hadResponse: !!error.response,
        
        // Error details
        errorName: error.name,
        errorStack: error.stack?.split('\n')[0] || 'none'
      };
      
      console.error('[API_DEBUG] receive-items ERROR:', diagnosticInfo);
      
      // Additional analysis
      if (error.code === 'ECONNABORTED' && !error.response) {
        console.error('[API_DEBUG] ANALYSIS: Request timed out with no response.', {
          possibleCauses: [
            '1. Request stuck in browser queue (check Network tab for "Stalled")',
            '2. DNS resolution failed',
            '3. SSL/TLS handshake hung',
            '4. Server never received request (check backend logs)',
            '5. Cloudflare/WAF blocked the request',
            '6. Browser extension interference'
          ],
          nextSteps: [
            'Check browser Network tab -> filter by "receive-items"',
            'Look at Timing breakdown (DNS, Connect, SSL, Waiting)',
            'Check if request has "Stalled" time > 0',
            'Try disabling browser extensions',
            'Check backend logs for request_id: ' + requestId
          ]
        });
      }
    }
    
    // ALWAYS log errors to help debug inventory loading issues
    logApiError(error, duration);
    
    // Check if this is a network error or timeout (offline scenario)
    const isNetworkError = !error.response || error.code === 'ECONNABORTED' || error.message === 'Network Error';
    
    // Handle offline queueing for write operations
    if (isNetworkError && originalRequest && ['post', 'put', 'delete'].includes(originalRequest.method?.toLowerCase())) {
      const fullUrl = `${API_BASE}${originalRequest.url}`;
      
      if (offlineQueue.shouldQueue(originalRequest.method, fullUrl)) {
        // Queue the request for later
        // Handle both string and object data
        let bodyData = originalRequest.data;
        if (typeof bodyData === 'string') {
          try {
            bodyData = JSON.parse(bodyData);
          } catch (e) {
            // Keep as is if not valid JSON
          }
        }
        
        await offlineQueue.enqueue(
          originalRequest.method,
          fullUrl,
          bodyData,
          originalRequest.headers
        );
        
        // Return a special error that indicates the action was queued
        const queuedError = new Error('Request queued for offline sync');
        queuedError.isQueued = true;
        queuedError.originalError = error;
        return Promise.reject(queuedError);
      }
    }
    
    // =========================================================================
    // WAREHOUSE UNAVAILABLE ERROR HANDLING (SCOPED RETRY)
    // =========================================================================
    // Retry logic triggers ONLY when ALL of these conditions are met:
    // 1. HTTP 404 status
    // 2. Error message contains warehouse-specific text
    // 3. Endpoint is in WAREHOUSE_AFFECTED_ENDPOINTS list
    // 4. warehouseFallbackHandler is registered
    // 5. This request hasn't already been retried (_warehouseRetry flag)
    // 
    // This ensures NO retries occur for unrelated 404s (reports, auth, items, etc.)
    // Retry happens exactly ONCE per request
    // =========================================================================
    if (error.response?.status === 404 && !originalRequest._warehouseRetry) {
      const detail = error.response?.data?.detail;
      const detailLower = (typeof detail === 'string' ? detail : '').toLowerCase();
      
      // Check for warehouse-specific error messages
      const isWarehouseErrorMessage = detailLower.includes('warehouse not found') || 
                                      detailLower.includes('warehouse has been archived') ||
                                      detailLower.includes('warehouse is unavailable');
      
      // Check if this endpoint is warehouse-affected (explicitly listed)
      const isWarehouseAffectedEndpoint = shouldIncludeWarehouseId(originalRequest.method, originalRequest.url);
      
      // Only proceed with retry if ALL conditions are met
      if (isWarehouseErrorMessage && isWarehouseAffectedEndpoint && warehouseFallbackHandler) {
        // Generate unique request key to prevent duplicate retries
        const requestKey = `${originalRequest.method}:${originalRequest.url}:${Date.now()}`;
        
        if (!pendingRetryRequests.has(requestKey)) {
          pendingRetryRequests.add(requestKey);
          // Mark request as retried - ensures exactly ONE retry per request
          originalRequest._warehouseRetry = true;
          
          try {
            // Trigger fallback to get new warehouse ID from WarehouseContext
            const newWarehouseId = await warehouseFallbackHandler();
            
            if (newWarehouseId) {
              // Update the request URL with new warehouse_id
              let updatedUrl = originalRequest.url;
              
              // Remove old warehouse_id from URL
              updatedUrl = updatedUrl.replace(/([?&])warehouse_id=[^&]*(&|$)/, (match, p1, p2) => {
                return p2 === '&' ? p1 : '';
              });
              
              // Clean up trailing ? or &
              updatedUrl = updatedUrl.replace(/[?&]$/, '');
              
              // Add new warehouse_id
              const separator = updatedUrl.includes('?') ? '&' : '?';
              updatedUrl = `${updatedUrl}${separator}warehouse_id=${newWarehouseId}`;
              
              originalRequest.url = updatedUrl;
              
              // Execute exactly ONE retry with the new warehouse_id
              const retryResult = await api(originalRequest);
              pendingRetryRequests.delete(requestKey);
              return retryResult;
            }
          } catch (retryError) {
            pendingRetryRequests.delete(requestKey);
            // Retry failed - do not retry again, continue with original error
            console.error('Warehouse fallback retry failed:', retryError);
          }
          
          pendingRetryRequests.delete(requestKey);
        }
      }
    }
    
    // Handle EMAIL_NOT_VERIFIED error (403)
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail;
      // Check if it's the EMAIL_NOT_VERIFIED error (can be object with code or string)
      const isEmailNotVerified = 
        (typeof detail === 'object' && detail?.code === 'EMAIL_NOT_VERIFIED') ||
        (typeof detail === 'string' && detail.includes('EMAIL_NOT_VERIFIED'));
      
      if (isEmailNotVerified) {
        // Only redirect if NOT already on the verify-email page
        // This prevents reload loops that fire duplicate PageView events
        if (!window.location.pathname.startsWith('/verify-email')) {
          window.location.href = '/verify-email';
        }
        return Promise.reject(error);
      }

      // Check if it's LEGAL_REQUIRED error
      const isLegalRequired =
        (typeof detail === 'object' && detail?.code === 'LEGAL_REQUIRED') ||
        (typeof detail === 'string' && detail.includes('LEGAL_REQUIRED'));

      if (isLegalRequired) {
        // Suppress hard redirect if legal was just accepted (stale request race)
        const withinSuppressWindow = (Date.now() - legalAcceptedTimestamp) < LEGAL_SUPPRESS_WINDOW_MS;
        if (withinSuppressWindow) {
          return Promise.reject(error);
        }
        if (!window.location.pathname.startsWith('/legal/accept')) {
          window.location.href = '/legal/accept';
        }
        return Promise.reject(error);
      }
    }
    
    // Handle BILLING_REQUIRED error (402 Payment Required)
    // This triggers when trial has expired and no active subscription
    if (error.response?.status === 402) {
      const detail = error.response?.data?.detail;
      const isBillingRequired = 
        (typeof detail === 'object' && detail?.code === 'BILLING_REQUIRED') ||
        (typeof detail === 'string' && detail.includes('BILLING_REQUIRED'));
      
      if (isBillingRequired) {
        // Get redirect path from response or default to /billing
        const redirectPath = (typeof detail === 'object' && detail?.redirect) || '/billing';
        
        // Only redirect if NOT already on allowed pages
        // These routes must be accessible when locked out for billing
        const allowedPaths = [
          '/subscription',    // Payment/plan selection
          '/billing',         // Billing management (only billing route - no /settings/billing)
          '/profile',         // Password change only (other API calls will 402)
          '/logout',          // Must be able to logout
          '/login',           // Login page
          '/verify-email',    // Email verification
          '/legal/accept',    // Legal acceptance
        ];
        const currentPath = window.location.pathname;
        const isOnAllowedPath = allowedPaths.some(path => currentPath.startsWith(path));
        
        if (!isOnAllowedPath) {
          window.location.href = redirectPath;
        }
        return Promise.reject(error);
      }
    }
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Use a shared refresh promise to deduplicate concurrent refresh attempts.
      // When multiple 401s fire simultaneously (getMe, billing, warehouses, etc.),
      // only the FIRST one actually calls /auth/refresh. The rest wait for its result.
      // This prevents the race condition where a second refresh consumes the old
      // refresh token after the first already replaced it.
      if (!window.__refreshPromise) {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          window.__refreshPromise = axios.post(`${API_BASE}/auth/refresh`, {
            refresh_token: refreshToken,
          }, { timeout: 15000 })
            .then((response) => {
              const { access_token, refresh_token: newRefreshToken } = response.data;
              localStorage.setItem('access_token', access_token);
              localStorage.setItem('refresh_token', newRefreshToken);
              return access_token;
            })
            .catch((refreshError) => {
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('user');
              window.location.href = '/login';
              throw refreshError;
            })
            .finally(() => {
              window.__refreshPromise = null;
            });
        }
      }
      
      if (window.__refreshPromise) {
        try {
          const newAccessToken = await window.__refreshPromise;
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Re-export cache utilities for manual invalidation
export const invalidateCache = apiCache.invalidate;
export const invalidateAllCaches = apiCache.invalidateAll;

// Auth API
export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  refresh: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { 
    current_password: currentPassword, 
    new_password: newPassword 
  }),
  // Email verification
  verifyEmail: (token) => api.get(`/auth/verify-email?token=${token}`),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  // Password reset
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  // Billing status
  getBillingStatus: () => api.get('/auth/billing-status'),
};

// Legal API
export const legalAPI = {
  accept: (data) => api.post('/legal/accept', data),
  getStatus: () => api.get('/legal/status'),
};

// Vans API
export const vansAPI = {
  list: (params) => api.get('/vans', { params }),
  get: (id) => api.get(`/vans/${id}`),
  getMyVehicle: () => api.get('/my-vehicle'),
  create: (data) => api.post('/vans', data),
  update: (id, data) => api.put(`/vans/${id}`, data),
  getInventory: (vanId) => api.get(`/vans/${vanId}/inventory`),
  useItem: (vanId, data) => api.post(`/vans/${vanId}/inventory/use`, data),
  restockItem: (vanId, data) => api.post(`/vans/${vanId}/inventory/restock`, data),
  bulkRestock: (vanId, data) => api.post(`/vans/${vanId}/inventory/bulk-restock`, data),
  // Update per-vehicle reorder threshold override for an item
  updateItemThreshold: (vanId, itemId, thresholdOverride) => 
    api.put(`/vans/${vanId}/inventory/${itemId}/threshold`, { reorder_threshold_override: thresholdOverride }),
  transfer: (data) => api.post('/transfers', data),
  // Authenticated export - returns blob for download
  exportInventory: async (vanId) => {
    const response = await api.get(`/vans/${vanId}/inventory/export.csv`, { responseType: 'blob' });
    return response;
  },
  compare: (vanIds) => api.get(`/vans/compare?van_ids=${vanIds.join(',')}`),
  getTechnicians: (vanId) => api.get(`/vans/${vanId}/technicians`),
  assignTechnicians: (vanId, techIds) => api.put(`/vans/${vanId}/technicians`, techIds),
  // Archive operations (owner-only)
  checkArchive: (vanId) => api.get(`/vans/${vanId}/archive-check`),
  archive: (vanId, techAction) => api.post(`/vans/${vanId}/archive`, techAction),
  restore: (vanId) => api.post(`/vans/${vanId}/restore`),
};

// Inventory Templates API
export const inventoryTemplatesAPI = {
  list: () => api.get('/inventory-templates'),
  get: (id) => api.get(`/inventory-templates/${id}`),
  create: (data) => api.post('/inventory-templates', data),
  apply: (id, data) => api.post(`/inventory-templates/${id}/apply`, data),
  delete: (id) => api.delete(`/inventory-templates/${id}`),
  clonePreview: (sourceVehicleId) => api.get(`/inventory-templates/clone-preview/${sourceVehicleId}`),
  cloneApply: (data) => api.post('/inventory-templates/clone-apply', data),
};

// Items API
export const itemsAPI = {
  list: (params) => api.get('/items', { params }),
  get: (id) => api.get(`/items/${id}`),
  create: (data) => api.post('/items', data),
  // Phase 1: Duplicate detection used by all part-creation flows.
  // Pass any subset of {name, sku, manufacturer_part_number, category_ids}
  // plus exclude_item_id when editing an existing item.
  checkDuplicates: (data) => api.post('/items/check-duplicates', data),
  // Phase 2: Find Duplicates + Merge Tool (owner-only)
  scanDuplicates: (data = {}) => api.post('/items/scan-duplicates', data),
  mergePreview: (data) => api.post('/items/merge-preview', data),
  merge: (data) => api.post('/items/merge', data),
  update: (id, data) => api.put(`/items/${id}`, data),
  uploadImage: (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/items/${id}/image`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteImage: (id) => api.delete(`/items/${id}/image`),
  getAvailabilityBreakdown: (id, locationType, locationId, inventoryId) =>
    api.get(`/items/${id}/availability-breakdown`, { params: { location_type: locationType, location_id: locationId, ...(inventoryId ? { inventory_id: inventoryId } : {}) } }),
  // Server-side search for typeahead (supports 18k+ item catalogs)
  search: (query, limit = 25, signal) => api.get('/items/search', { 
    params: { q: query || '', limit },
    signal 
  }),
  // Bulk operations (legacy - update category/threshold)
  bulkUpdateCategory: (itemIds, categoryId) => api.put('/items/bulk/category', { item_ids: itemIds, category_id: categoryId }),
  bulkUpdateReorderThreshold: (itemIds, threshold) => api.put('/items/bulk/reorder_threshold', { item_ids: itemIds, reorder_threshold: threshold }),
  bulkDelete: (itemIds) => api.delete('/items/bulk', { data: { item_ids: itemIds } }),
  // Authenticated export - returns blob for download
  exportSelected: async (itemIds, { search, categoryId, excludeIds } = {}) => {
    const params = new URLSearchParams();
    if (itemIds && itemIds.length > 0) params.set('ids', itemIds.join(','));
    if (search) params.set('search', search);
    if (categoryId && categoryId !== 'all') params.set('category_id', categoryId);
    if (excludeIds) params.set('exclude_ids', excludeIds);
    const qs = params.toString();
    const response = await api.get(`/items/export${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
    return response;
  },
  // Archive operations (soft delete)
  archive: (itemId, opts = {}) => api.delete(`/items/${itemId}${opts.force ? '?force=true' : ''}`),  // DELETE = archive (soft delete)
  archivedWithStock: () => api.get('/items/archived-with-stock'),
  restore: (itemId) => api.post(`/items/${itemId}/restore`),
  // Hard delete (permanent, blocked if references exist)
  hardDelete: (itemId) => api.delete(`/items/${itemId}/hard`),
  // Bulk archive (inline for ≤500 items)
  bulkArchive: (itemIds) => api.post('/items/bulk-archive', { item_ids: itemIds }),
  // Bulk archive (job-based for large sets)
  bulkArchiveJob: (itemIds) => api.post('/items/bulk-archive-job', { item_ids: itemIds }),
  // Bulk archive by query (for "Select all matching")
  bulkArchiveByQuery: (categoryId, search) => api.post('/items/bulk-archive-query', { category_id: categoryId, search: search }),
  // Bulk hard delete (always job-based)
  bulkHardDelete: (itemIds) => api.post('/items/bulk-hard-delete', { item_ids: itemIds }),
  // Bulk hard delete by query (for "Select all matching")  
  bulkHardDeleteByQuery: (categoryId, search) => api.post('/items/bulk-hard-delete-query', { category_id: categoryId, search: search }),
  // Bulk remove category (unassign items from category)
  bulkRemoveCategory: (data) => api.post('/items/bulk-remove-category', data),
  // Get bulk job status
  getBulkJobStatus: (jobId) => api.get(`/items/bulk-job/${jobId}`),
  // Count items matching query (for "Select all X matching")
  bulkCount: (categoryId, search) => api.post('/items/bulk-count', { category_id: categoryId, search: search }),
};

// Category Groups API
export const categoryGroupsAPI = {
  list: () => api.get('/category-groups'),
  create: (data) => api.post('/category-groups', data),
  update: (id, data) => api.put(`/category-groups/${id}`, data),
  delete: (id) => api.delete(`/category-groups/${id}`),
};

// Categories API (legacy flat)
export const categoriesAPI = {
  list: (params) => api.get('/categories', { params }),
  listWithCounts: () => api.get('/categories/with-counts'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  bulkDelete: (categoryIds) => api.post('/categories/bulk-delete', { category_ids: categoryIds }),
};

// Part Categories API (nested hierarchy)
export const partCategoriesAPI = {
  tree: () => api.get('/part-categories/tree'),
  browse: (parentId) => api.get('/part-categories/browse', { params: parentId ? { parent_id: parentId } : {} }),
  create: (data) => api.post('/part-categories', data),
  update: (id, data) => api.put(`/part-categories/${id}`, data),
  delete: (id, force = false) => api.delete(`/part-categories/${id}`, { params: force ? { force: true } : {} }),
  bulkSave: (categories, deletedIds) => api.post('/part-categories/bulk-save', { categories, deleted_ids: deletedIds }),
};

// Catalogue Import API (no quantities, no destination)
export const catalogueImportAPI = {
  parseCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/catalogue/parse-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  validate: (file, mapping) => {
    const formData = new FormData();
    formData.append('file', file);
    // Clean mapping - remove empty strings
    const cleanMapping = {};
    Object.entries(mapping).forEach(([k, v]) => {
      if (v && v !== '' && v !== 'none') {
        cleanMapping[k] = v;
      }
    });
    return api.post(`/catalogue/validate-import?mapping=${encodeURIComponent(JSON.stringify(cleanMapping))}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  execute: (data) => api.post('/catalogue/execute-import', data),
  // File-based async import - sends raw CSV file, no client-side parsing
  executeAsyncFile: (file, mapping) => {
    const formData = new FormData();
    formData.append('file', file);
    // Clean mapping - remove empty strings
    const cleanMapping = {};
    Object.entries(mapping).forEach(([k, v]) => {
      if (v && v !== '' && v !== 'none') {
        cleanMapping[k] = v;
      }
    });
    return api.post(`/catalogue/execute-import/async?mapping=${encodeURIComponent(JSON.stringify(cleanMapping))}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Legacy JSON-based async import (for small imports only)
  executeAsync: (data, idempotencyKey) => {
    const params = idempotencyKey ? `?idempotency_key=${encodeURIComponent(idempotencyKey)}` : '';
    return api.post(`/catalogue/execute-import/async/json${params}`, data);
  },
};

// Jobs API for polling background job status
export const jobsAPI = {
  get: (jobId) => api.get(`/jobs/${jobId}`),
  list: (params) => api.get('/jobs', { params }),
  cancel: (jobId) => api.post(`/jobs/${jobId}/cancel`),
  // Retry import with legacy job handling - returns needs_reupload for old jobs
  retryImport: (jobId) => api.post(`/catalogue/import/${jobId}/retry`),
};

// Legacy Inventory Import API (kept for backwards compatibility)
export const inventoryImportAPI = {
  parseCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/catalogue/parse-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  validate: (file, mapping) => {
    const formData = new FormData();
    formData.append('file', file);
    // Clean mapping - remove empty strings
    const cleanMapping = {};
    Object.entries(mapping).forEach(([k, v]) => {
      if (v && v !== '' && v !== 'none') {
        cleanMapping[k] = v;
      }
    });
    return api.post(`/catalogue/validate-import?mapping=${encodeURIComponent(JSON.stringify(cleanMapping))}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  execute: (data) => api.post('/catalogue/execute-import', data),
  getFleetInventory: () => api.get('/inventory/fleet'),
};

// Warehouse Import API (with quantities)
export const warehouseImportAPI = {
  parseCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/warehouse/parse-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  validate: (file, mapping) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/warehouse/validate-import?mapping=${encodeURIComponent(JSON.stringify(mapping))}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  execute: (data) => api.post('/warehouse/execute-import', data),
};

// Events API
export const eventsAPI = {
  list: (params) => api.get('/events', { params }),
  get: (id) => api.get(`/events/${id}`),
  getComments: (id) => api.get(`/events/${id}/comments`),
  addComment: (id, commentText) => api.post(`/events/${id}/comments`, { comment: commentText }),
  // Authenticated export - returns blob for download
  exportCsv: async (params) => {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v) query.append(k, v);
    });
    const response = await api.get(`/events/export.csv?${query.toString()}`, { responseType: 'blob' });
    return response;
  },
};

// Vehicle Comments API
export const vehicleCommentsAPI = {
  list: (vanId) => api.get(`/vans/${vanId}/comments`),
  add: (vanId, message) => api.post(`/vans/${vanId}/comments`, { message }),
};

// Item Comments API
export const itemCommentsAPI = {
  list: (itemId) => api.get(`/items/${itemId}/comments`),
  add: (itemId, message) => api.post(`/items/${itemId}/comments`, { message }),
};

// Conversations/Messaging API
export const messagingAPI = {
  listConversations: () => api.get('/conversations'),
  createDM: (userId) => api.post('/conversations/dm', { user_id: userId }),
  getVehicleThread: (vanId) => api.get(`/vans/${vanId}/thread`),
  getMessages: (conversationId, params) => api.get(`/conversations/${conversationId}/messages`, { params }),
  sendMessage: (conversationId, body) => api.post(`/conversations/${conversationId}/messages`, { body }),
  deleteMessage: (messageId) => api.delete(`/messages/${messageId}`),
};

// Channels API
export const channelsAPI = {
  list: () => api.get('/channels/'),
  create: (data) => api.post('/channels/', data),
  get: (id) => api.get(`/channels/${id}`),
  update: (id, data) => api.put(`/channels/${id}`, data),
  archive: (id) => api.delete(`/channels/${id}`),
  // Members
  listMembers: (id) => api.get(`/channels/${id}/members`),
  addMembers: (id, userIds) => api.post(`/channels/${id}/members`, { user_ids: userIds }),
  removeMember: (channelId, userId) => api.delete(`/channels/${channelId}/members/${userId}`),
  updateMemberRole: (channelId, userId, role) => api.put(`/channels/${channelId}/members/${userId}/role`, { role }),
  join: (id) => api.post(`/channels/${id}/join`),
  updateNotifyMode: (id, mode) => api.put(`/channels/${id}/notify`, { mode }),
  // Messages
  listMessages: (id, params) => api.get(`/channels/${id}/messages`, { params }),
  sendMessage: (id, bodyRaw) => api.post(`/channels/${id}/messages`, { body_raw: bodyRaw }),
  deleteMessage: (channelId, messageId) => api.delete(`/channels/${channelId}/messages/${messageId}`),
  // Reactions
  toggleReaction: (channelId, messageId, emoji) => api.post(`/channels/${channelId}/messages/${messageId}/react`, { emoji }),
  // Attachments
  uploadAttachment: (channelId, messageId, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/channels/${channelId}/messages/${messageId}/attachments`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  serveAttachment: (attachmentId) => `${api.defaults.baseURL}/channels/attachments/${attachmentId}/serve`,
  serveAttachmentBlob: (attachmentId) => api.get(`/channels/attachments/${attachmentId}/serve`, { responseType: 'arraybuffer' }),
  // Read status
  markRead: (id) => api.post(`/channels/${id}/mark_read`),
  getUnreadCounts: () => api.get('/channels/unread/counts'),
  // Mentions
  searchMentions: (q, types) => api.get('/channels/mentions/search', { params: { q, types } }),
};

// AI Actions API
export const aiActionsAPI = {
  getStatus: () => api.get('/ai-actions/status'),
  getSystemUser: () => api.get('/ai-actions/system-user'),
  getUsage: (params) => api.get('/ai-actions/usage', { params }),
  getCreditCosts: () => api.get('/ai-actions/credit-costs'),
  enable: (tier = 'lite') => api.post('/ai-actions/enable', { tier }),
  disable: () => api.post('/ai-actions/disable'),
  updateTier: (tier) => api.put('/ai-actions/tier', { tier }),
};

// AI Proposals API
export const aiProposalsAPI = {
  list: (params) => api.get('/ai-proposals/proposals', { params }),
  get: (id) => api.get(`/ai-proposals/proposals/${id}`),
  approve: (id, data) => api.post(`/ai-proposals/proposals/${id}/approve`, data || {}),
  reject: (id, data) => api.post(`/ai-proposals/proposals/${id}/reject`, data || {}),
  execute: (id) => api.post(`/ai-proposals/proposals/${id}/execute`),
  cancel: (id) => api.post(`/ai-proposals/proposals/${id}/cancel`),
  edit: (id, data) => api.put(`/ai-proposals/proposals/${id}`, data),
  dashboard: () => api.get('/ai-proposals/dashboard'),
};

// Reorder API
export const reorderAPI = {
  list: () => api.get('/reorder'),
};

// Search API
export const searchAPI = {
  search: (q, options = {}) => api.get('/search', { params: { q }, ...options }),
};

// Dashboard API
export const dashboardAPI = {
  get: () => api.get('/dashboard'),
  getInventoryOverview: () => api.get('/dashboard/inventory-overview'),
};

// Users API
export const usersAPI = {
  list: (params) => api.get('/users', { params }),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  uploadPhoto: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/users/${id}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Archive operations (owner-only)
  archive: (userId, force = false) => api.post(`/users/${userId}/archive${force ? '?force=true' : ''}`),
  hardDelete: (userId) => api.delete(`/users/${userId}/hard-delete`),
  restore: (userId) => api.post(`/users/${userId}/restore`),
};

// Adjustment Requests API
export const adjustmentsAPI = {
  list: (params) => api.get('/adjustment-requests', { params }),
  create: (data) => api.post('/adjustment-requests', data),
  approve: (id) => api.post(`/adjustment-requests/${id}/approve`),
  reject: (id) => api.post(`/adjustment-requests/${id}/reject`),
};

// Phase 3.5: Unified Approvals API (server-side pagination)
export const approvalsAPI = {
  list: (params, config = {}) => api.get('/approvals', { params, ...config }),
};

// Warehouse API
export const warehouseAPI = {
  get: () => api.get('/warehouse'),
  update: (name) => api.put(`/warehouse?name=${encodeURIComponent(name)}`),
  getInventory: (params) => api.get('/warehouse/inventory', { params }),
  restock: (data) => api.post('/warehouse/inventory/restock', data),
  use: (data) => api.post('/warehouse/inventory/use', data),
  bulkRestock: (data) => api.post('/warehouse/inventory/bulk-restock', data),
  updateItem: (itemId, data) => api.put(`/warehouse/inventory/${itemId}`, data),
  bulkUpdateThreshold: (data) => api.post('/warehouse/inventory/bulk-threshold', data),
  // Universal transfer endpoint - body only, no query params
  transfer: (data) => api.post('/warehouse/transfers', data),
  exportCsv: async (params) => {
    const response = await api.get('/warehouse/export', { params, responseType: 'blob' });
    return response;
  },
  // Threshold CSV Import
  previewThresholdImport: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/warehouse/thresholds/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  applyThresholdImport: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/warehouse/thresholds/import/apply', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Multi-warehouse endpoints (Phase 2)
  listWarehouses: () => api.get('/warehouses'),
  createWarehouse: (data) => api.post('/warehouses', data),
  updateWarehouse: (id, data) => api.put(`/warehouses/${id}`, data),
  archiveWarehouse: (id) => api.delete(`/warehouses/${id}`),
  setDefaultWarehouse: (id) => api.post(`/warehouses/${id}/set-default`),
  // Plan limits
  getPlanLimits: () => api.get('/plan-limits'),
  // Warehouse settings (including consigned inventory feature flag)
  getSettings: () => api.get('/warehouse/settings'),
  updateSettings: (data) => api.patch('/warehouse/settings', data),
  // Company branding (for printable POs)
  getCompanyBranding: () => api.get('/company-branding'),
  updateCompanyBranding: (data) => api.patch('/company-branding', data),
  // Job Allocation
  assignToJob: (data) => api.post('/warehouse/inventory/assign-to-job', data),
  requestMatches: (params) => api.get('/warehouse/part-request-matches', { params }),
  listJobAllocations: (params) => api.get('/warehouse/job-allocations', { params }),
};

// Internationalization Settings API
export const i18nSettingsAPI = {
  get: () => api.get('/i18n-settings'),
  update: (data) => api.patch('/i18n-settings', data),
};

// Warehouse-to-Warehouse Transfer API
export const whTransferAPI = {
  listPending: (warehouseId) => api.get('/warehouse-transfers/pending', { params: { warehouse_id: warehouseId } }),
  listAll: (params) => api.get('/warehouse-transfers/all', { params }),
  accept: (transferId) => api.post(`/warehouse-transfers/${transferId}/accept`),
  cancel: (transferId) => api.post(`/warehouse-transfers/${transferId}/cancel`),
};

// Beta Feedback API
export const feedbackAPI = {
  submit: (data) => api.post('/feedback', data),
};

// Reports API
export const reportsAPI = {
  list: () => api.get('/reports'),
  run: (data) => api.post('/reports/run', data),
  export: async (data) => {
    const response = await api.post('/reports/export', data, { responseType: 'blob' });
    return response;
  },
  // Technician Scorecards
  scorecards: (params) => api.get('/technician-scorecards/', { params }),
  scorecardDetail: (techId, params) => api.get(`/technician-scorecards/${techId}/detail`, { params }),
  // Filter options (Phase 1 - PO)
  getStatuses: () => api.get('/reports/filters/statuses'),
  getTechnicians: () => api.get('/reports/filters/technicians'),
  getVendors: () => api.get('/reports/filters/vendors'),
  // Filter options (Phase 2 - Inventory)
  getItems: () => api.get('/reports/filters/items'),
  getVehicles: () => api.get('/reports/filters/vehicles'),
  getActors: () => api.get('/reports/filters/actors'),
  getEventTypes: () => api.get('/reports/filters/event-types'),
  getCategories: () => api.get('/reports/filters/categories'),
  getLocationTypes: () => api.get('/reports/filters/location-types'),
  // Filter options (Phase 4 - Approvals)
  getApprovalStatuses: () => api.get('/reports/filters/approval-statuses'),
  getApprovalTypes: () => api.get('/reports/filters/approval-types'),
  getApprovers: () => api.get('/reports/filters/approvers'),
  getRequesters: () => api.get('/reports/filters/requesters'),
  // Filter options (Part Request → PO report)
  getWarehouses: () => api.get('/reports/filters/warehouses'),
  getDepartments: () => api.get('/reports/filters/departments'),
  // Report History (server-persisted with pagination - Phase 3.5)
  getHistory: (params = {}) => api.get('/reports/history', { params }),
  createHistory: (data) => api.post('/reports/history', data),
  updateHistory: (historyId, data) => api.put(`/reports/history/${historyId}`, data),
  togglePin: (historyId) => api.patch(`/reports/history/${historyId}/pin`),
  clearHistory: (includePinned = false) => api.delete('/reports/history', { data: { include_pinned: includePinned } }),
  // Phase 3.5: Paginated report results
  getResults: (reportRunId, params = {}) => api.get(`/reports/results/${reportRunId}`, { params }),
  // Report Templates (saved presets - no results stored)
  getTemplates: () => api.get('/reports/templates'),
  createTemplate: (data) => api.post('/reports/templates', data),
  updateTemplate: (templateId, data) => api.patch(`/reports/templates/${templateId}`, data),
  deleteTemplate: (templateId) => api.delete(`/reports/templates/${templateId}`),
};

// Scheduled Reports API
export const scheduledReportsAPI = {
  list: () => api.get('/scheduled-reports'),
  create: (data) => api.post('/scheduled-reports', data),
  update: (id, data) => api.patch(`/scheduled-reports/${id}`, data),
  remove: (id) => api.delete(`/scheduled-reports/${id}`),
  toggle: (id) => api.post(`/scheduled-reports/${id}/toggle`),
  runNow: (id) => api.post(`/scheduled-reports/${id}/run-now`),
  getRuns: (id) => api.get(`/scheduled-reports/${id}/runs`),
};

// MFA API
export const mfaAPI = {
  getStatus: () => api.get('/mfa/status'),
  setup: () => api.post('/mfa/setup'),
  setupWithToken: (tempToken) => api.post('/mfa/setup-with-token', { temp_token: tempToken }),
  confirm: (code) => api.post('/mfa/confirm', { code }),
  confirmWithToken: (tempToken, code) => api.post('/mfa/confirm-with-token', { temp_token: tempToken, code }),
  disable: (password, code) => api.post('/mfa/disable', { password, code }),
  // Owner endpoints
  setUserRequired: (userId, mfaRequired) => api.post(`/mfa/team/${userId}/required`, { mfa_required: mfaRequired }),
  getTenantSecurity: () => api.get('/mfa/tenant/security'),
  setTenantSecurity: (mfaRequiredAll) => api.post('/mfa/tenant/security', { mfa_required_all: mfaRequiredAll }),
};

// Auth MFA verification
export const verifyMFA = (mfaToken, code) => api.post('/auth/mfa/verify', { mfa_token: mfaToken, code });

// Notifications API
export const notificationsAPI = {
  list: (params) => api.get('/notifications', { params }),
  getCount: () => api.get('/notifications/count'),
  markAllRead: () => api.post('/notifications/mark_all_read'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
};

// PO Notification Settings API (Tenant defaults for PO received emails)
export const poNotificationSettingsAPI = {
  get: () => api.get('/po-notification-settings'),
  update: (data) => api.patch('/po-notification-settings', data),
};

// Notification Engine Settings API
export const notificationSettingsAPI = {
  get: () => api.get('/company-settings/notifications'),
  update: (data) => api.put('/company-settings/notifications', data),
  sendTestEmail: () => api.post('/company-settings/notifications/test-email'),
};

// Daily Digest Email API
export const dailyDigestAPI = {
  getSettings: () => api.get('/company-settings/daily-digest'),
  updateSettings: (data) => api.patch('/company-settings/daily-digest', data),
  preview: () => api.post('/company-settings/daily-digest/preview'),
};

// Unread Messages API
export const unreadMessagesAPI = {
  getCount: () => api.get('/messages/unread_count'),
  markConversationRead: (conversationId) => api.post(`/conversations/${conversationId}/mark_read`),
};

// Barcode/QR Scanning API
export const scanAPI = {
  lookup: (code) => api.get(`/scan/lookup?code=${encodeURIComponent(code)}`),
  assign: (data) => api.post('/scan/assign', data),
};

// Item Codes API
export const itemCodesAPI = {
  list: (itemId) => api.get(`/items/${itemId}/codes`),
  add: (itemId, data) => api.post(`/items/${itemId}/codes`, data),
  delete: (codeId) => api.delete(`/item-codes/${codeId}`),
  getLabel: (itemId) => api.get(`/items/${itemId}/label`),
};

// Tenant Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  getInventoryDisplay: () => api.get('/settings/inventory-display'),
  updateInventoryDisplay: (data) => api.put('/settings/inventory-display', data),
  saveMyInventoryView: (view, prefs) => api.put(`/settings/inventory-display/me/${view}`, prefs),
  resetMyInventoryView: (view) => api.delete(`/settings/inventory-display/me/${view}`),
};

// Purchase Orders API
export const purchaseOrdersAPI = {
  list: (params = {}) => api.get('/purchase-orders', { params: { slim: true, ...params } }),  // Slim mode for list view
  get: (id) => api.get(`/purchase-orders/${id}`),
  create: (data, requestId) => api.post('/purchase-orders', data, { timeout: 60000, headers: requestId ? { 'Idempotency-Key': requestId } : {} }),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  exportCsv: async (format = 'detailed') => {
    const response = await api.get(`/purchase-orders/export/csv?format=${format}`, { responseType: 'blob' });
    return response;
  },
  // Preview PO number for a warehouse (does not increment sequence)
  previewNumber: (warehouseId) => api.get(`/purchase-orders/preview-number/${warehouseId}`),
  // Email PO to vendor (Phase 3) – async job, returns 202 + job_id
  emailVendor: (poId, data) => api.post(`/purchase-orders/${poId}/email-vendor`, data),
  // Poll email job status
  getEmailJobStatus: (jobId) => api.get(`/purchase-orders/email-jobs/${jobId}`),
  // Phase 4: PO Departments
  listDepartments: (includeInactive = false) => api.get('/purchase-orders/departments', { params: { include_inactive: includeInactive } }),
  createDepartment: (data) => api.post('/purchase-orders/departments', data),
  updateDepartment: (id, data) => api.patch(`/purchase-orders/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/purchase-orders/departments/${id}`),
  // P2: Line-item receiving
  receiveItems: (poId, items, options = {}) => api.post(`/purchase-orders/${poId}/receive-items`, { items }, options),
  markAllReceived: (poId) => api.post(`/purchase-orders/${poId}/mark-all-received`),
  undoReceive: (poId) => api.post(`/purchase-orders/${poId}/undo-receive`),
  // Manual recovery: retry the "PO Fully Received" email when the original
  // auto-send silently failed. Owner/office only on the backend.
  resendFullyReceivedEmail: (poId, confirmPossibleDuplicate = false) => api.post(`/purchase-orders/${poId}/resend-fully-received-email`, null, { params: confirmPossibleDuplicate ? { confirm_possible_duplicate: true } : {} }),
  retryNotification: (poId, notificationId, confirmPossibleDuplicate = false) => api.post(`/purchase-orders/${poId}/notifications/${notificationId}/retry`, { confirm_possible_duplicate: confirmPossibleDuplicate }),
  getNotificationIssues: () => api.get('/purchase-orders/notification-issues'),
  // Support diagnostic: dry-run inspector for the PO Fully Received notification.
  // Returns resolved To/CC, all input layers (global/warehouse-default/warehouse-override/rule),
  // and the precise reason a send would skip — without sending an email.
  getEmailDiagnostics: (poId) => api.get(`/purchase-orders/${poId}/email-diagnostics`),
  // Phase 3: PO Inventory Assignment (after fully received)
  getAssignmentDefaults: (poId, mode = 'assign') => api.get(`/purchase-orders/${poId}/assignment-defaults`, { params: { mode } }),
  assignReceivedInventory: (poId, assignments, jobId, receive) => api.post(`/purchase-orders/${poId}/assign-received`, { assignments, ...(jobId ? { job_id: jobId } : {}), ...(receive ? { receive } : {}) }, { timeout: 120000 }),
  // Activity log
  getActivity: (poId) => api.get(`/purchase-orders/${poId}/activity`),
  // Phase 6: server-side paged list
  listPaged: (params, { fresh = false } = {}) => api.get('/purchase-orders/paged', { params, noCache: fresh }),
  // Phase 8: PO documents
  listDocuments: (poId) => api.get(`/purchase-orders/${poId}/documents`),
  uploadDocument: (poId, file, label, source, onUploadProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('label', label);
    fd.append('source', source);
    return api.post(`/purchase-orders/${poId}/documents`, fd, { timeout: 300000, onUploadProgress, headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getDocumentFile: (poId, docId) => api.get(`/purchase-orders/${poId}/documents/${docId}/file`, { responseType: 'blob', timeout: 120000 }),
  removeDocument: (poId, docId) => api.delete(`/purchase-orders/${poId}/documents/${docId}`),
  // CRM Upgrade Safety: Job Linking
  jobLinkPreview: () => api.get('/purchase-orders/job-link-preview'),
  jobLinkApply: () => api.post('/purchase-orders/job-link-apply'),
};

// PO Drafts API — server-side draft persistence
export const poDraftsAPI = {
  list: () => api.get('/purchase-orders/drafts'),
  get: (id) => api.get(`/purchase-orders/drafts/${id}`),
  create: (data) => api.post('/purchase-orders/drafts', data),
  update: (id, data) => api.put(`/purchase-orders/drafts/${id}`, data),
  delete: (id) => api.delete(`/purchase-orders/drafts/${id}`),
  submit: (id) => api.post(`/purchase-orders/drafts/${id}/submit`, {}, { timeout: 60000, headers: { 'Idempotency-Key': `draft-submit-${id}` } }),
  duplicate: (id) => api.post(`/purchase-orders/drafts/${id}/duplicate`),
};

// Phase 5A: Part Requests API
export const partRequestsAPI = {
  // List & Count
  list: (params = {}) => api.get('/part-requests', { params }),
  getPendingCount: () => api.get('/part-requests/count'),
  
  // CRUD
  get: (id) => api.get(`/part-requests/${id}`),
  create: (data) => api.post('/part-requests', data),
  update: (id, data) => api.put(`/part-requests/${id}`, data),
  delete: (id) => api.delete(`/part-requests/${id}`),
  
  // Status Actions
  approve: (id) => api.post(`/part-requests/${id}/approve`),
  cancel: (id, data = {}) => api.post(`/part-requests/${id}/cancel`, data),
  
  // Convert to PO
  convertToPO: (id) => api.post(`/part-requests/${id}/convert-to-po`),
  
  // Phase 6B: Staging (Ready-to-Schedule Workflow)
  stage: (id, data) => api.post(`/part-requests/${id}/stage`, data),
  unstage: (id, data = {}) => api.post(`/part-requests/${id}/unstage`, data),
  getJobReadiness: (jobNumber) => api.get(`/part-requests/job/${encodeURIComponent(jobNumber)}/readiness`),
  
  // PHASE 3A: Inventory Allocation (In Stock Path)
  checkAvailability: (id) => api.get(`/part-requests/${id}/availability`),
  fulfillFromInventory: (id, data = {}) => api.post(`/part-requests/${id}/fulfill-from-inventory`, data),
  
  // Consumption Layer
  getAllocations: (id) => api.post(`/part-requests/${id}/allocations`),
  consume: (id, data) => api.post(`/part-requests/${id}/consume`, data),
  vehicleConsume: (data) => api.post('/part-requests/vehicle-consume', data),
  repairReservations: () => api.post('/part-requests/repair-reservations'),

  // Return to Inventory
  returnToInventory: (id, data) => api.post(`/part-requests/${id}/return-to-inventory`, data),
};

// Quarantine API (Phase 5B: Warranty Hold Tracking)
export const quarantineAPI = {
  // List & Summary
  list: (params = {}) => api.get('/quarantine', { params }),
  getSummary: () => api.get('/quarantine/summary'),
  
  // CRUD
  get: (id) => api.get(`/quarantine/${id}`),
  create: (data) => api.post('/quarantine', data),
  
  // Actions
  resolve: (id, data) => api.post(`/quarantine/${id}/resolve`, data),
  
  // Check item quarantine status
  checkItem: (itemId, params = {}) => api.get(`/quarantine/check/item/${itemId}`, { params }),
};

// Job Kits API (Phase 6C: Kit Grouping)
export const jobKitsAPI = {
  // List & Get
  list: (params = {}) => api.get('/job-kits', { params }),
  get: (id) => api.get(`/job-kits/${id}`),
  
  // CRUD
  create: (data) => api.post('/job-kits', data),
  update: (id, data) => api.patch(`/job-kits/${id}`, data),
  delete: (id) => api.delete(`/job-kits/${id}`),
  
  // Kit Management
  addRequests: (id, requestIds) => api.post(`/job-kits/${id}/add-requests`, { part_request_ids: requestIds }),
  removeRequests: (id, requestIds) => api.post(`/job-kits/${id}/remove-requests`, { part_request_ids: requestIds }),
  close: (id) => api.post(`/job-kits/${id}/close`),
  reopen: (id) => api.post(`/job-kits/${id}/reopen`),
};

// Job Timeline API (Phase 5C: Job-Centric PO Timeline)
export const jobTimelineAPI = {
  // Search jobs
  search: (q, limit = 10) => api.get('/job-timeline/search', { params: { q, limit } }),
  
  // Get full timeline
  getTimeline: (jobNumber, includeQuarantine = true) => 
    api.get(`/job-timeline/${encodeURIComponent(jobNumber)}`, { params: { include_quarantine: includeQuarantine } }),
  
  // Get summary
  getSummary: (jobNumber) => api.get(`/job-timeline/${encodeURIComponent(jobNumber)}/summary`),
};

// Vendors API (Full management - new standalone endpoints)
export const vendorsAPI = {
  // List with search and filters
  list: (params) => api.get('/vendors', { params }),
  // Get single vendor
  get: (id) => api.get(`/vendors/${id}`),
  // Create new vendor
  create: (data) => api.post('/vendors', data),
  // Update vendor
  update: (id, data) => api.put(`/vendors/${id}`, data),
  // Delete vendor (force=true for deactivation when has POs)
  delete: (id, force = false) => api.delete(`/vendors/${id}`, { params: { force } }),
  // CSV Export
  exportCSV: (params) => api.get('/vendors/export/csv', { params, responseType: 'blob' }),
  // CSV Template download
  downloadTemplate: () => api.get('/vendors/template/csv', { responseType: 'blob' }),
  // CSV Import
  importCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/vendors/import/csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Legacy endpoints (for PO dropdown compatibility)
  listForPO: () => api.get('/purchase-orders/vendors'),
  createFromPO: (data) => api.post('/purchase-orders/vendors', data),
};

// Vehicle-to-Warehouse Transfer Requests API
export const vehicleToWarehouseAPI = {
  list: (params) => api.get('/vehicle-to-warehouse-requests', { params }),
  create: (data) => api.post('/vehicle-to-warehouse-requests', data),
  approve: (id) => api.post(`/vehicle-to-warehouse-requests/${id}/approve`),
  deny: (id, denialReason) => api.post(`/vehicle-to-warehouse-requests/${id}/deny`, { denial_reason: denialReason }),
};

// Vehicle-to-Vehicle Transfer Requests API
export const vehicleToVehicleAPI = {
  list: (params) => api.get('/vehicle-to-vehicle-requests', { params }),
  create: (data) => api.post('/vehicle-to-vehicle-requests', data),
  approve: (id) => api.post(`/vehicle-to-vehicle-requests/${id}/approve`),
  deny: (id, denialReason) => api.post(`/vehicle-to-vehicle-requests/${id}/deny`, { denial_reason: denialReason }),
};

// Manual Inventory Adjustments API (Owner only)
export const manualAdjustmentsAPI = {
  create: (data) => api.post('/manual-adjustment', data),
};

// Audit API (Phase 3: Controlled Adjustments)
// Note: Backend routes remain /audits (alias approach - DB tables unchanged)
export const auditAPI = {
  list: (params) => api.get('/audits', { params }),
  get: (id) => api.get(`/audits/${id}`),
  create: (data) => api.post('/audits', data),
  recordCount: (auditId, itemId, data) => api.put(`/audits/${auditId}/items/${itemId}`, data),
  complete: (id) => api.post(`/audits/${id}/complete`),
  abandon: (id) => api.post(`/audits/${id}/abandon`),
  applyAdjustments: (id) => api.post(`/audits/${id}/apply-adjustments`),
  getLastCompleted: (targetType, targetId) => api.get('/audits/last-completed', {
    params: { target_type: targetType, target_id: targetId }
  }),
  getInsights: (params) => api.get('/audits/insights', { params }),
};

// Cycle Count API - Alias for auditAPI (UI rename, backend unchanged)
export const cycleCountAPI = auditAPI;

// Billing API
export const billingAPI = {
  // Pricing
  getPricing: () => api.get('/billing/pricing'),
  
  // Subscription Status
  getStatus: () => api.get('/billing/status'),
  getSubscription: () => api.get('/billing/subscription'),
  
  // Beta & Trial Status (for banner display)
  getBetaTrialStatus: () => api.get('/billing/beta-trial-status'),
  dismissBetaBanner: (bannerType = 'beta_sunset') => api.post('/billing/dismiss-beta-banner', { banner_type: bannerType }),
  
  // Checkout
  createCheckout: (tierId, originUrl, interval = 'monthly') => api.post('/billing/checkout', {
    tier_id: tierId,
    interval: interval,
    origin_url: originUrl
  }),
  
  // Customer Portal
  createPortal: (returnUrl) => api.post('/billing/portal', {
    return_url: returnUrl
  }),
  
  // Change Plan
  changePlan: (newTierId, newInterval = 'month') => api.post('/billing/change-plan', {
    new_tier_id: newTierId,
    new_interval: newInterval
  }),
  
  // Beta Toggle (disabled for tenants)
  toggleBeta: (isBeta) => api.post('/billing/beta-toggle', { is_beta: isBeta }),
  
  // Referrals
  getReferralStats: () => api.get('/billing/referral-stats'),
  getReferralCode: () => api.get('/billing/referral-code'),
  validateReferralCode: (code) => api.post('/billing/validate-referral-code', null, {
    params: { code }
  }),

  // Trial config (public, no auth)
  getTrialConfig: () => api.get('/billing/trial-config'),
  // Trial card collection (auth required)
  createTrialCheckout: (originUrl) => api.post('/billing/trial-checkout', {
    origin_url: originUrl
  }),
};

// Referrals API
export const referralsAPI = {
  getMyReferrals: () => api.get('/referrals/me'),
  getTenantStats: () => api.get('/referrals/tenant'),
};

// Tools API - Tool checkout and assignment tracking
export const toolsAPI = {
  list: (params) => api.get('/tools', { params }),
  get: (id) => api.get(`/tools/${id}`),
  create: (data) => api.post('/tools', data),
  update: (id, data) => api.put(`/tools/${id}`, data),
  delete: (id) => api.delete(`/tools/${id}`),
  checkout: (id, data) => api.post(`/tools/${id}/checkout`, data),
  returnTool: (id, data) => api.post(`/tools/${id}/return`, data),
  getHistory: (id, limit) => api.get(`/tools/${id}/history`, { params: { limit } }),
  getCheckedOutSummary: () => api.get('/tools/checked-out/summary'),
  // Categories
  listCategories: () => api.get('/tools/categories'),
  createCategory: (name) => api.post('/tools/categories', { name }),
  deleteCategory: (categoryId) => api.delete(`/tools/categories/${categoryId}`),
  // Image upload
  uploadImage: (id, formData) => api.post(`/tools/${id}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteImage: (id) => api.delete(`/tools/${id}/image`),
  // Import/Export
  exportCsv: async () => {
    const response = await api.get('/tools/export', { responseType: 'blob' });
    return response;
  },
  importCsv: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/tools/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Admin API (uses separate token storage)
// Higher timeout for admin endpoints which aggregate data across all tenants
const adminApi = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60000,
});

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export { adminApi };

export const adminAPI = {
  reservationResetPreview: (tenantId) => adminApi.get(`/admin/maintenance/reservation-reset/${tenantId}/preview`),
  reservationResetExecute: (tenantId, body) => adminApi.post(`/admin/maintenance/reservation-reset/${tenantId}/execute`, body, { timeout: 180000 }),
  reservationResetVerify: (tenantId, resetId) => adminApi.get(`/admin/maintenance/reservation-reset/${tenantId}/verify/${resetId}`),
  reservationResetFile: (tenantId, resetId, kind) => adminApi.get(`/admin/maintenance/reservation-reset/${tenantId}/${kind}/${resetId}.csv`, { responseType: 'blob' }),
  login: (email, password) => adminApi.post('/admin/login', { email, password }),
  getMe: () => adminApi.get('/admin/me'),
  getStatus: () => adminApi.get('/admin/status'),
  getFeedback: (params) => adminApi.get('/admin/feedback', { params }),
  updateFeedbackStatus: (id, status) => adminApi.post(`/admin/feedback/${id}/status`, { status }),
  getFeedbackStats: () => adminApi.get('/admin/feedback/stats'),
  getFeedbackInsights: () => adminApi.get('/admin/feedback/insights'),
  getNotificationStatus: () => adminApi.get('/admin/notification-status'),
  sendTestFeedback: () => adminApi.post('/admin/test-feedback'),
  createTestFeedbackFull: () => adminApi.post('/admin/test-feedback-full'),
  
  // Tenant Management
  getTenantStats: () => adminApi.get('/admin/tenant-stats'),
  getTenants: (params) => adminApi.get('/admin/tenants', { params }),
  getTenant: (id) => adminApi.get(`/admin/tenants/${id}`),
  deactivateTenant: (id) => adminApi.post(`/admin/tenants/${id}/deactivate`),
  activateTenant: (id) => adminApi.post(`/admin/tenants/${id}/activate`),
  softDeleteTenant: (id) => adminApi.post(`/admin/tenants/${id}/soft-delete`),
  restoreTenant: (id) => adminApi.post(`/admin/tenants/${id}/restore`),
  resetOwnerPassword: (id) => adminApi.post(`/admin/tenants/${id}/reset-owner-password`),
  resetUserPassword: (userId) => adminApi.post(`/admin/users/${userId}/reset-password`),
  
  // Beta Status Management (admin-only)
  getTenantBetaStatus: (id) => adminApi.get(`/admin/tenants/${id}/beta`),
  setTenantBetaStatus: (id, isBeta) => adminApi.put(`/admin/tenants/${id}/beta`, null, { params: { is_beta: isBeta } }),
  
  // Add-on Bypass (admin-only)
  toggleAddonBypass: (id, allow) => adminApi.post(`/admin/tenants/${id}/addon-bypass`, { allow_addons_without_subscription: allow }),
  
  // CRM Beta Toggle (admin-only)
  toggleCrmBeta: (id, enabled, durationDays) => adminApi.put(`/admin/tenants/${id}/crm-beta`, { enabled, duration_days: durationDays }),
  
  // Stripe Test Checkout (Platform Admin Only)
  // Guarded by ENABLE_STRIPE_TEST_TOOLS env var on backend
  getStripeToolsStatus: () => adminApi.get('/admin/stripe/status'),
  getStripeConfig: () => adminApi.get('/admin/stripe/config'),
  createTestCheckout: (data) => adminApi.post('/admin/stripe/test-checkout', data),
  getRecentStripeSessions: (limit = 10) => adminApi.get('/admin/stripe/recent-sessions', { params: { limit } }),
  verifyStripeSession: (sessionId) => adminApi.get(`/admin/stripe/verify-session/${sessionId}`),
  
  // Hard Delete (Dev Admin Only) - with configurable allowlist and risk assessment
  getTestDomains: () => adminApi.get('/admin/test-domains'),
  addTestDomain: (domain) => adminApi.post('/admin/test-domains', { domain }),
  removeTestDomain: (id) => adminApi.delete(`/admin/test-domains/${id}`),
  listTestTenants: () => adminApi.get('/admin/test-tenants'),
  bulkHardDeleteDryRun: (forceRisky = false) =>
    adminApi.post('/admin/test-tenants/bulk-hard-delete/dry-run', { force_risky: forceRisky, confirm: 'DRY-RUN' }, { timeout: 240000 }),
  bulkHardDeleteExecute: (confirmPhrase, forceRisky = false, confirmForce = null) =>
    adminApi.post('/admin/test-tenants/bulk-hard-delete', {
      confirm: confirmPhrase,
      force_risky: forceRisky,
      confirm_force: confirmForce
    }),
  getPurgeJob: (jobId) => adminApi.get(`/admin/purge-jobs/${jobId}`),
  hardDeleteTenant: (id, confirmPhrase, forceRisky = false, confirmForce = null) => 
    adminApi.delete(`/admin/test-tenants/${id}/hard`, { 
      data: { 
        confirm: confirmPhrase,
        force_risky: forceRisky,
        confirm_force: confirmForce
      } 
    }),
  getAuditLogs: (params) => adminApi.get('/admin/audit-logs', { params }),
  
  // DEV-ONLY: Single Tenant Purge (Permanent Delete)
  previewTenantPurge: (tenantId) => adminApi.get(`/admin/tenants/${tenantId}/purge-preview`),
  purgeTenant: (tenantId, tenantNameConfirm, confirmIrreversible) => 
    adminApi.post(`/admin/tenants/${tenantId}/purge`, {
      tenant_name_confirm: tenantNameConfirm,
      confirm_irreversible: confirmIrreversible
    }),
  
  // DEV-ONLY: Purge Audit Trail
  getPurgeAudit: (params) => adminApi.get('/admin/purge-audit', { params }),
  getPurgeAuditDetail: (auditId) => adminApi.get(`/admin/purge-audit/${auditId}`),
  getPurgeAuditStats: (days = 30) => adminApi.get('/admin/purge-audit/stats/summary', { params: { days } }),

  // Twilio Webhook Health (drift detection)
  getTwilioWebhookStatus: (tenantId) =>
    adminApi.get('/admin/twilio/webhook-status', { params: tenantId ? { tenant_id: tenantId } : {} }),
  
  // Impersonation (Support Tools)
  startImpersonation: (tenantId, readOnly = false) => 
    adminApi.post(`/admin/tenants/${tenantId}/impersonate`, { read_only: readOnly }),
  validateImpersonation: (token) => 
    adminApi.get('/admin/impersonation/validate', { 
      headers: { 'X-Impersonation-Token': token } 
    }),
  exitImpersonation: (token) => 
    adminApi.post('/admin/impersonation/exit', null, { 
      headers: { 'X-Impersonation-Token': token } 
    }),
  revokeImpersonation: (sessionId) => 
    adminApi.post(`/admin/impersonation/${sessionId}/revoke`),
  
  // Screen Share Support Sessions
  createSupportSession: (tenantId) => 
    adminApi.post(`/admin/tenants/${tenantId}/support-sessions`),
  getSupportSession: (sessionId) => 
    adminApi.get(`/admin/support-sessions/${sessionId}`),
  endSupportSession: (sessionId) => 
    adminApi.post(`/admin/support-sessions/${sessionId}/end`),
  
  // Comped Billing
  billingOverride: (id, data) => adminApi.post(`/admin/tenants/${id}/billing-override`, data),
  createCompedTenant: (data) => adminApi.post('/admin/tenants/comped-create', data),

  // Testing hygiene
  markTestTenant: (id, isTest = true) => adminApi.patch(`/admin/tenants/${id}/mark-test`, null, { params: { is_test: isTest } }),
  completeOnboarding: (id) => adminApi.post(`/admin/tenants/${id}/complete-onboarding`),
  cleanupTestTenants: (dryRun = true) => adminApi.delete('/admin/tenants/cleanup-test', { params: { dry_run: dryRun } }),

  // Platform Revenue
  getPlatformRevenue: () => adminApi.get('/admin/platform-revenue'),
  getPlatformRevenueTransactions: (limit = 20) => adminApi.get('/admin/platform-revenue/transactions', { params: { limit } }),

  // Admin Team Management
  getTeam: () => adminApi.get('/admin/team'),
  createTeamInvite: (data) => adminApi.post('/admin/team/invite', data),
  resendTeamInvite: (inviteId) => adminApi.post(`/admin/team/invite/${inviteId}/resend`),
  revokeTeamInvite: (inviteId) => adminApi.delete(`/admin/team/invite/${inviteId}`),
  disableAdmin: (adminId) => adminApi.post(`/admin/team/${adminId}/disable`),
  enableAdmin: (adminId) => adminApi.post(`/admin/team/${adminId}/enable`),
  hardDeleteAdmin: (adminId) => adminApi.delete(`/admin/team/${adminId}/hard-delete`),
  hardDeleteInvite: (inviteId) => adminApi.delete(`/admin/team/invite/${inviteId}/hard-delete`),
  getStripeTestStatus: () => adminApi.get('/admin/stripe/status'),

  // Marketing Contacts V1
  getMarketingStats: () => adminApi.get('/admin/marketing/contacts/stats'),
  getMarketingContacts: (params) => adminApi.get('/admin/marketing/contacts', { params }),
  getMarketingContact: (id) => adminApi.get(`/admin/marketing/contacts/${id}`),
  updateMarketingContact: (id, data) => adminApi.patch(`/admin/marketing/contacts/${id}`, data),
  deleteMarketingContact: (id) => adminApi.delete(`/admin/marketing/contacts/${id}`),
  addMarketingContact: (data) => adminApi.post('/admin/marketing/contacts', data),
  backfillMarketingContacts: () => adminApi.post('/admin/marketing/contacts/backfill'),
  getMarketingContactEvents: (id) => adminApi.get(`/admin/marketing/contacts/${id}/events`),
  removeMarketingSuppression: (email) => adminApi.delete(`/admin/marketing/suppressions/${encodeURIComponent(email)}`),

  // Cleanup Queue
  getCleanupStats: () => adminApi.get('/admin/cleanup-queue/stats'),
  getCleanupTenants: (params) => adminApi.get('/admin/cleanup-queue/tenants', { params }),
  runCleanupCheck: (dryRun = true) => adminApi.post(`/admin/cleanup-queue/run-check?dry_run=${dryRun}`),
  exemptTenant: (tenantId, reason) => adminApi.post(`/admin/cleanup-queue/tenants/${tenantId}/exempt`, { reason }),
  removeExemption: (tenantId) => adminApi.post(`/admin/cleanup-queue/tenants/${tenantId}/remove-exemption`),
  approvePurge: (tenantId) => adminApi.post(`/admin/cleanup-queue/tenants/${tenantId}/approve-purge`, { confirm_phrase: "APPROVE PURGE" }),
  getCleanupLogs: (params) => adminApi.get('/admin/cleanup-queue/logs', { params }),
};

// Support Session API (Tenant-facing)
export const supportAPI = {
  getPendingSession: () => api.get('/support-sessions/pending'),
  acceptSession: (sessionId) => api.post(`/support-sessions/${sessionId}/accept`),
  declineSession: (sessionId) => api.post(`/support-sessions/${sessionId}/decline`),
  stopSharing: (sessionId) => api.post(`/support-sessions/${sessionId}/stop`),
  getSessionStatus: (sessionId) => api.get(`/support-sessions/${sessionId}/status`),
};

// Support Tickets API (In-app support system)
export const supportTicketsAPI = {
  // User endpoints
  getStatus: () => api.get('/support-tickets/status'),
  getMyTickets: () => api.get('/support-tickets/tickets/me'),
  getTicket: (id) => api.get(`/support-tickets/tickets/${id}`),
  createTicket: (data) => api.post('/support-tickets/tickets', data),
  addMessage: (ticketId, data) => api.post(`/support-tickets/tickets/${ticketId}/messages`, data),
  closeTicket: (ticketId) => api.patch(`/support-tickets/tickets/${ticketId}`),
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/support-tickets/upload-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Permissions API
export const permissionsAPI = {
  getMyPermissions: () => api.get('/me/permissions'),
  getAllPermissions: () => api.get('/permissions'),
  getUserPermissions: (userId) => api.get(`/users/${userId}/permissions`),
  updateUserPermissions: (userId, permissions) => api.put(`/users/${userId}/permissions`, { permissions }),
  resetUserPermissions: (userId) => api.post(`/users/${userId}/permissions/reset`),
  updateUserRole: (userId, role) => api.put(`/users/${userId}/role`, { role }),
};

// Permission Templates API
export const templateAPI = {
  list: () => api.get('/permission-templates'),
  create: (data) => api.post('/permission-templates', data),
  update: (id, data) => api.patch(`/permission-templates/${id}`, data),
  remove: (id) => api.delete(`/permission-templates/${id}`),
  apply: (id, userId) => api.post(`/permission-templates/${id}/apply`, { user_id: userId }),
};

// Onboarding API - Non-blocking engagement system
export const onboardingAPI = {
  getStatus: () => api.get('/onboarding/status'),
  dismissWelcome: () => api.post('/onboarding/dismiss-welcome'),
  dismissNudge: () => api.post('/onboarding/dismiss-nudge'),
  markBooked: () => api.post('/onboarding/mark-booked'),
};

// Entitlements API - Stage 0: Feature gating
export const entitlementsAPI = {
  get: () => api.get('/entitlements/'),
  update: (data) => api.put('/entitlements/', data),
};

// Customers API - Stage 1: CRM Foundation
export const customersAPI = {
  list: (params) => api.get('/customers/', { params }),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers/', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  remove: (id) => api.delete(`/customers/${id}`),
  // Locations
  createLocation: (customerId, data) => api.post(`/customers/${customerId}/locations`, data),
  updateLocation: (customerId, locationId, data) => api.put(`/customers/${customerId}/locations/${locationId}`, data),
  removeLocation: (customerId, locationId) => api.delete(`/customers/${customerId}/locations/${locationId}`),
  // Property Profile
  getProperty: (customerId) => api.get(`/customers/${customerId}/property`),
  upsertProperty: (customerId, data) => api.put(`/customers/${customerId}/property`, data),
  enrichProperty: (customerId) => api.post(`/customers/${customerId}/property/enrich`),
  exportCSV: (search) => api.get('/customers/export/csv', { params: { search }, responseType: 'blob' }),
  getSummary: (customerId) => api.get(`/customers/${customerId}/summary`),
  // Customer Intelligence
  listAttachments: (customerId) => api.get(`/customers/${customerId}/attachments`),
  uploadAttachment: (customerId, formData) => api.post(`/customers/${customerId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAttachment: (customerId, attachmentId) => api.delete(`/customers/${customerId}/attachments/${attachmentId}`),
  getActivity: (customerId, params) => api.get(`/customers/${customerId}/activity`, { params }),
};

export const leadSourcesAPI = {
  list: () => api.get('/lead-sources/'),
  create: (data) => api.post('/lead-sources/', data),
  report: (days = 90) => api.get('/lead-sources/report', { params: { days } }),
};

// CRM Jobs API - Stage 2: CRM Jobs
export const crmJobsAPI = {
  list: (params) => api.get('/crm-jobs/', { params }),
  get: (id) => api.get(`/crm-jobs/${id}`),
  create: (data) => api.post('/crm-jobs/', data),
  update: (id, data) => api.put(`/crm-jobs/${id}`, data),
  remove: (id) => api.delete(`/crm-jobs/${id}`),
  readiness: (id) => api.get(`/crm-jobs/${id}/readiness`),
  exportCSV: (params) => api.get('/crm-jobs/export/csv', { params, responseType: 'blob' }),
  nextNumber: () => api.get('/crm-jobs/next-number'),
  // Labor time tracking
  timeLogs: (jobId) => api.get(`/crm-jobs/${jobId}/time-logs`),
  clockIn: (jobId, data) => api.post(`/crm-jobs/${jobId}/time-logs/clock-in`, data || {}),
  clockOut: (jobId, data) => api.post(`/crm-jobs/${jobId}/time-logs/clock-out`, data || {}),
  deleteTimeLog: (jobId, logId) => api.delete(`/crm-jobs/${jobId}/time-logs/${logId}`),
  laborSummary: (jobId) => api.get(`/crm-jobs/${jobId}/labor-summary`),
  // OMW + Notification log
  sendOMW: (jobId) => api.post(`/crm-jobs/${jobId}/omw`),
  notificationLog: (jobId) => api.get(`/crm-jobs/${jobId}/notification-log`),
};

// Job Attachments API (photos/files on jobs)
export const jobAttachmentsAPI = {
  list: (jobId) => api.get(`/jobs/${jobId}/attachments`),
  upload: (jobId, file, caption) => {
    const fd = new FormData();
    fd.append('file', file);
    if (caption) fd.append('caption', caption);
    return api.post(`/jobs/${jobId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  remove: (jobId, attachmentId) => api.delete(`/jobs/${jobId}/attachments/${attachmentId}`),
};

// Tags API
export const tagsAPI = {
  list: (params) => api.get('/tags/', { params }),
  create: (data) => api.post('/tags/', data),
  update: (id, data) => api.put(`/tags/${id}`, data),
  remove: (id) => api.delete(`/tags/${id}`),
  merge: (data) => api.post('/tags/merge', data),
};

// Dispatch API - Stage 3: Dispatch Events
export const dispatchAPI = {
  listEvents: (params) => api.get('/dispatch/events/', { params }),
  createEvent: (data) => api.post('/dispatch/events/', data),
  updateEvent: (id, data) => api.put(`/dispatch/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/dispatch/events/${id}`),
  unscheduledJobs: (params) => api.get('/dispatch/events/unscheduled-jobs', { params }),
  sendOtwText: (eventId, data) => api.post(`/dispatch/events/${eventId}/send-otw-text`, data || {}),
};

// Tech Blocked Time API — Technician availability/blocked time CRUD
export const techBlockedTimeAPI = {
  list: (params) => api.get('/tech-blocked-time/', { params }),
  create: (data) => api.post('/tech-blocked-time/', data),
  update: (id, data) => api.put(`/tech-blocked-time/${id}`, data),
  delete: (id) => api.delete(`/tech-blocked-time/${id}`),
};

// Job Materials API - Stage 4: Job Materials (Truck Material Sync)
export const jobMaterialsAPI = {
  list: (jobId, params) => api.get(`/jobs/${jobId}/materials`, { params }),
  add: (jobId, data) => api.post(`/jobs/${jobId}/materials`, data),
  reverse: (jobId, materialId) => api.delete(`/jobs/${jobId}/materials/${materialId}`),
  summary: (jobId) => api.get(`/jobs/${jobId}/materials/summary`),
  defaultSource: (jobId) => api.get(`/jobs/${jobId}/materials/default-source`),
  confirm: (jobId, materialId, data) => api.post(`/jobs/${jobId}/materials/${materialId}/confirm`, data),
  bulkConfirm: (jobId, data) => api.post(`/jobs/${jobId}/materials/bulk-confirm`, data),
  returnToInventory: (jobId, materialId, data) => api.post(`/jobs/${jobId}/materials/${materialId}/return`, data),
};

// Usage Reports API - Stage 4.5C: Inventory Usage Analytics
export const usageReportsAPI = {
  usage: (params) => api.get('/reports/usage', { params }),
};

// Low Stock Intelligence API - Stage 6
export const lowStockAPI = {
  usageVelocity: () => api.get('/inventory/usage-velocity'),
  depletionPredictions: (params) => api.get('/inventory/depletion-predictions', { params }),
  restockSuggestions: (params) => api.get('/inventory/restock-suggestions', { params }),
};

// Invoice Drafts API - Stage 7: Parts to Invoice Automation
export const invoiceDraftsAPI = {
  suggestedLines: (jobId) => api.get(`/jobs/${jobId}/suggested-invoice-lines`),
  createDraft: (jobId) => api.post(`/jobs/${jobId}/create-invoice-draft`),
  listDrafts: (jobId) => api.get(`/jobs/${jobId}/invoice-drafts`),
  getDraft: (draftId) => api.get(`/invoice-drafts/${draftId}`),
  updateDraft: (draftId, data) => api.put(`/invoice-drafts/${draftId}`, data),
  finalizeDraft: (draftId) => api.post(`/invoice-drafts/${draftId}/finalize`),
};

// Weekly Truck Prep Report API - Stage 23
export const weeklyTruckReportAPI = {
  getWeekly: (params) => api.get('/truck-prep-report/weekly', { params }),
  downloadPdf: (params) => api.get('/truck-prep-report/weekly/pdf', { params, responseType: 'blob' }),
};

// =============================================================================
// DEBUG UTILITIES - Call from browser console to diagnose issues
// =============================================================================
// Enable detailed API logging: localStorage.setItem('DEBUG_API', 'true')
// View stored errors: window.getApiErrors()
// Clear stored errors: window.clearApiErrors()

if (typeof window !== 'undefined') {
  window.getApiErrors = () => {
    try {
      const errors = JSON.parse(localStorage.getItem('API_ERRORS') || '[]');
      console.table(errors);
      return errors;
    } catch (e) {
      console.log('No stored errors');
      return [];
    }
  };
  
  window.clearApiErrors = () => {
    localStorage.removeItem('API_ERRORS');
    console.log('API errors cleared');
  };
  
  window.enableApiDebug = () => {
    localStorage.setItem('DEBUG_API', 'true');
    console.log('API debug logging enabled. Refresh the page to see logs.');
  };
  
  window.disableApiDebug = () => {
    localStorage.removeItem('DEBUG_API');
    console.log('API debug logging disabled.');
  };
}


// --- Estimates API (Stage 33) ---
export const estimatesAPI = {
  list: (params) => api.get('/estimates/', { params }),
  get: (id) => api.get(`/estimates/${id}`),
  create: (data) => api.post('/estimates/', data),
  update: (id, data) => api.put(`/estimates/${id}`, data),
  remove: (id) => api.delete(`/estimates/${id}`),
  addItem: (id, data) => api.post(`/estimates/${id}/items`, data),
  updateItem: (itemId, data) => api.put(`/estimates/items/${itemId}`, data),
  removeItem: (itemId) => api.delete(`/estimates/items/${itemId}`),
  assign: (id, data) => api.post(`/estimates/${id}/assign`, data),
  addNote: (id, data) => api.post(`/estimates/${id}/notes`, data),
  convertToJob: (id) => api.post(`/estimates/${id}/convert-to-job`),
  createOption: (id, data) => api.post(`/estimates/${id}/options`, data),
  updateOption: (optionId, data) => api.put(`/estimates/options/${optionId}`, data),
  removeOption: (optionId) => api.delete(`/estimates/options/${optionId}`),
  selectOption: (optionId) => api.post(`/estimates/options/${optionId}/select`),
  addOptionMedia: (optionId, data) => api.post(`/estimates/options/${optionId}/media`, data),
  removeMedia: (mediaId) => api.delete(`/estimates/media/${mediaId}`),
  generateLink: (id) => api.post(`/estimates/${id}/generate-link`),
  revokeLink: (id) => api.post(`/estimates/${id}/revoke-link`),
  sendApproval: (id, data) => api.post(`/estimates/${id}/send-approval`, data),
  sendText: (id) => api.post(`/estimates/${id}/send-text`),
  downloadPdf: (id) => api.get(`/estimates/${id}/pdf`, { responseType: 'blob' }),
  getDefaults: () => api.get('/company-settings/estimate-defaults'),
  updateDefaults: (data) => api.put('/company-settings/estimate-defaults', data),
  // Upsells
  createUpsell: (id, data) => api.post(`/estimates/${id}/upsells`, data),
  updateUpsell: (id, upsellId, data) => api.put(`/estimates/${id}/upsells/${upsellId}`, data),
  removeUpsell: (id, upsellId) => api.delete(`/estimates/${id}/upsells/${upsellId}`),
};

export const invoicesAPI = {
  list: (params) => api.get('/invoices/', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices/', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  remove: (id) => api.delete(`/invoices/${id}`),
  addItem: (id, data) => api.post(`/invoices/${id}/items`, data),
  updateItem: (itemId, data) => api.put(`/invoices/items/${itemId}`, data),
  removeItem: (itemId) => api.delete(`/invoices/items/${itemId}`),
  fromEstimate: (estimateId) => api.post(`/invoices/from-estimate/${estimateId}`),
  fromJob: (jobId) => api.post(`/invoices/from-job/${jobId}`),
  forJob: (jobId) => api.get(`/invoices/for-job/${jobId}`),
  jobsForCustomer: (customerId) => api.get(`/invoices/jobs-for-customer/${customerId}`),
  markPaid: (id, data) => api.post(`/invoices/${id}/mark-paid`, data || {}),
  voidInvoice: (id) => api.post(`/invoices/${id}/void`),
  send: (id, data) => api.post(`/invoices/${id}/send`, data),
  publicView: (token) => api.get(`/invoices/public/${token}`),
  downloadPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  publicPdf: (token) => api.get(`/invoices/public/${token}/pdf`, { responseType: 'blob' }),
  createCheckout: (token, data) => api.post(`/invoices/public/${token}/checkout`, data),
  paymentStatus: (token, sessionId) => api.get(`/invoices/public/${token}/payment-status`, { params: { session_id: sessionId } }),
  payoutStatus: (id) => api.get(`/invoices/${id}/payout-status`),
  // Payment management
  addPayment: (id, data) => api.post(`/invoices/${id}/payments`, data),
  listPayments: (id) => api.get(`/invoices/${id}/payments`),
  deletePayment: (invoiceId, paymentId) => api.delete(`/invoices/${invoiceId}/payments/${paymentId}`),
  reversePayment: (invoiceId, paymentId, data) => api.post(`/invoices/${invoiceId}/payments/${paymentId}/reverse`, data),
  correctStatus: (invoiceId) => api.post(`/invoices/${invoiceId}/correct-status`),
  auditLog: (invoiceId) => api.get(`/invoices/${invoiceId}/audit-log`),
};

export const priceBookAPI = {
  listCategories: (params) => api.get('/price-book/categories', { params }),
  createCategory: (data) => api.post('/price-book/categories', data),
  updateCategory: (id, data) => api.put(`/price-book/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/price-book/categories/${id}`),
  // Industries
  listIndustries: (params) => api.get('/price-book/industries', { params }),
  createIndustry: (data) => api.post('/price-book/industries', data),
  updateIndustry: (id, data) => api.put(`/price-book/industries/${id}`, data),
  deleteIndustry: (id) => api.delete(`/price-book/industries/${id}`),
  backfillIndustries: () => api.post('/price-book/industries/backfill'),
  uploadIndustryImage: (id, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/price-book/industries/${id}/image`, fd); },
  deleteIndustryImage: (id) => api.delete(`/price-book/industries/${id}/image`),
  uploadCategoryImage: (id, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/price-book/categories/${id}/image`, fd); },
  deleteCategoryImage: (id) => api.delete(`/price-book/categories/${id}/image`),
  uploadItemImage: (id, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/price-book/items/${id}/image`, fd); },
  deleteItemImage: (id) => api.delete(`/price-book/items/${id}/image`),
  listItems: (params) => api.get('/price-book/items', { params }),
  createItem: (data) => api.post('/price-book/items', data),
  getItem: (id) => api.get(`/price-book/items/${id}`),
  updateItem: (id, data) => api.put(`/price-book/items/${id}`, data),
  deleteItem: (id) => api.delete(`/price-book/items/${id}`),
  addComponent: (id, data) => api.post(`/price-book/items/${id}/components`, data),
  updateComponent: (id, data) => api.put(`/price-book/components/${id}`, data),
  deleteComponent: (id) => api.delete(`/price-book/components/${id}`),
  // Material recipes (inventory consumption)
  addRecipeLine: (itemId, data) => api.post(`/price-book/items/${itemId}/recipes`, data),
  bulkSaveRecipes: (itemId, lines) => api.put(`/price-book/items/${itemId}/recipes/bulk`, { lines }),
  deleteRecipeLine: (id) => api.delete(`/price-book/recipes/${id}`),
  addToEstimate: (itemId, estimateId, data) => api.post(`/price-book/items/${itemId}/add-to-estimate/${estimateId}`, data),
  addToInvoice: (itemId, invoiceId, data) => api.post(`/price-book/items/${itemId}/add-to-invoice/${invoiceId}`, data),
  getSettings: () => api.get('/price-book/settings'),
  updateSettings: (data) => api.put('/price-book/settings', data),
  exportItemsCsv: () => api.get('/price-book/export/items', { responseType: 'blob' }),
  exportComponentsCsv: () => api.get('/price-book/export/components', { responseType: 'blob' }),
  exportCategoriesCsv: () => api.get('/price-book/export/categories', { responseType: 'blob' }),
  importItemsCsv: (file, mode = 'skip', dryRun = false) => { const fd = new FormData(); fd.append('file', file); return api.post(`/price-book/import/items?mode=${mode}&dry_run=${dryRun}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  importComponentsCsv: (file, mode = 'skip', dryRun = false) => { const fd = new FormData(); fd.append('file', file); return api.post(`/price-book/import/components?mode=${mode}&dry_run=${dryRun}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  // Templates
  listTemplates: (params) => api.get('/price-book/templates', { params }),
  createTemplate: (data) => api.post('/price-book/templates', data),
  updateTemplate: (id, data) => api.put(`/price-book/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/price-book/templates/${id}`),
  // Starter Packs
  listStarterPacks: () => api.get('/price-book/starter-packs'),
  previewStarterPack: (industry) => api.get(`/price-book/starter-packs/${industry}/preview`),
  installStarterPack: (industry) => api.post(`/price-book/starter-packs/${industry}/install`),
  getStarterPackSettings: () => api.get('/price-book/starter-packs/settings'),
  updateStarterPackSettings: (data) => api.put('/price-book/starter-packs/settings', data),
  // Bulk price adjustment
  bulkPriceAdjust: (data) => api.post('/price-book/items/bulk-price-adjust', data),
  getHcpPricebook: (params) => api.get('/price-book/hcp-pricebook', { params }),
  // CRM Import
  previewImport: (file, sourceFormat) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = sourceFormat ? `?source_format=${sourceFormat}` : '';
    return api.post(`/price-book/import/preview${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  executeImport: (file, sourceFormat) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = sourceFormat ? `?source_format=${sourceFormat}` : '';
    return api.post(`/price-book/import/execute${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export default api;

export const stripeConnectAPI = {
  onboard: (data) => api.post('/stripe/connect/onboard', data),
  status: () => api.get('/stripe/connect/status'),
  disconnect: () => api.post('/stripe/connect/disconnect'),
};

export const importsAPI = {
  upload: (entityType, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/imports/upload?entity_type=${entityType}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  listSessions: (entityType) => api.get('/imports/sessions', { params: entityType ? { entity_type: entityType } : {} }),
  getSession: (sessionId, statusFilter) => api.get(`/imports/sessions/${sessionId}`, { params: statusFilter ? { status: statusFilter } : {} }),
  correctRow: (rowId, correctedData) => api.patch(`/imports/rows/${rowId}`, { corrected_data: correctedData }),
  reimport: (sessionId) => api.post(`/imports/sessions/${sessionId}/reimport`),
};

export const userPreferencesAPI = {
  get: () => api.get('/user/preferences'),
  update: (data) => api.put('/user/preferences', data),
};


// CRM Import API (Customers & Jobs)
export const crmImportAPI = {
  upload: (fileOrFormData, entityType, sourceSystem = 'generic') => {
    if (fileOrFormData instanceof FormData) {
      return api.post('/crm-import/upload', fileOrFormData, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    const formData = new FormData();
    formData.append('file', fileOrFormData);
    formData.append('entity_type', entityType);
    formData.append('source_system', sourceSystem);
    return api.post('/crm-import/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  confirmMapping: (sessionId, columnMapping) => api.post(`/crm-import/${sessionId}/mapping`, { column_mapping: columnMapping }),
  preview: (sessionId) => api.get(`/crm-import/${sessionId}/preview`),
  execute: (sessionId) => api.post(`/crm-import/${sessionId}/execute`),
  executeStatus: (sessionId) => api.get(`/crm-import/${sessionId}/execute-status`),
  rollback: (sessionId) => api.post(`/crm-import/${sessionId}/rollback`),
  sessions: (entityType) => api.get('/crm-import/sessions', { params: { entity_type: entityType } }),
  // V2 endpoints
  getReviewRows: (sessionId) => api.get(`/crm-import/${sessionId}/review`),
  resolveRow: (sessionId, rowId, action, matchId) => api.post(`/crm-import/${sessionId}/review/${rowId}/resolve`, { action, match_id: matchId }),
  batchResolve: (sessionId, action, matchStatusFilter = 'duplicate') => api.post(`/crm-import/${sessionId}/batch-resolve`, { action, match_status_filter: matchStatusFilter }),
  getSummary: (sessionId) => api.get(`/crm-import/${sessionId}/summary`),
  listTemplates: (entityType) => api.get('/crm-import/templates/list', { params: { entity_type: entityType } }),
  saveTemplate: (data) => api.post('/crm-import/templates', data),
  deleteTemplate: (templateId) => api.delete(`/crm-import/templates/${templateId}`),
  assignableUsersCount: () => api.get('/crm-import/assignable-users-count'),
};

// ── Marketing Center ──
export const marketingAPI = {
  // Reviews
  listReviews: (params) => api.get('/marketing/reviews', { params }),
  getReview: (id) => api.get(`/marketing/reviews/${id}`),
  updateReview: (id, data) => api.patch(`/marketing/reviews/${id}`, data),
  replyToReview: (id, data) => api.post(`/marketing/reviews/${id}/reply`, data),
  getReviewStats: () => api.get('/marketing/reviews/stats'),
  // Connections
  listConnections: () => api.get('/marketing/connections'),
  createConnection: (data) => api.post('/marketing/connections', data),
  deleteConnection: (id) => api.delete(`/marketing/connections/${id}`),
  // Google Business Profile Integration
  googleStatus: () => api.get('/integrations/google/status'),
  googleConnect: () => api.get('/integrations/google/connect'),
  googleAccounts: (state) => api.get('/integrations/google/accounts', { params: { state } }),
  googleLocations: (accountName, state) => api.get(`/integrations/google/accounts/${encodeURIComponent(accountName)}/locations`, { params: { state } }),
  googleSaveLocations: (data) => api.post('/integrations/google/locations/save', data),
  googleSyncReviews: (connectionId) => api.post(`/integrations/google/connections/${connectionId}/sync`),
  googlePostReply: (reviewId, data) => api.post(`/integrations/google/reviews/${reviewId}/post-reply`, data),
  googleDisconnect: (connectionId) => api.delete(`/integrations/google/connections/${connectionId}`),
};

// ---- Marketing Campaigns ----
export const campaignsAPI = {
  // Campaigns
  list: (params) => api.get('/marketing/campaigns', { params }),
  get: (id) => api.get(`/marketing/campaigns/${id}`),
  create: (data) => api.post('/marketing/campaigns', data),
  update: (id, data) => api.put(`/marketing/campaigns/${id}`, data),
  delete: (id) => api.delete(`/marketing/campaigns/${id}`),
  // Campaign emails
  addEmail: (campaignId, data) => api.post(`/marketing/campaigns/${campaignId}/emails`, data),
  updateEmail: (campaignId, emailId, data) => api.put(`/marketing/campaigns/${campaignId}/emails/${emailId}`, data),
  deleteEmail: (campaignId, emailId) => api.delete(`/marketing/campaigns/${campaignId}/emails/${emailId}`),
  // Exit conditions
  addExitCondition: (campaignId, data) => api.post(`/marketing/campaigns/${campaignId}/exit-conditions`, data),
  deleteExitCondition: (campaignId, condId) => api.delete(`/marketing/campaigns/${campaignId}/exit-conditions/${condId}`),
  // Recipients
  listRecipients: (campaignId, params) => api.get(`/marketing/campaigns/${campaignId}/recipients`, { params }),
  // Events
  listEvents: (campaignId, params) => api.get(`/marketing/campaigns/${campaignId}/events`, { params }),
  // Analytics
  getAnalytics: (campaignId) => api.get(`/marketing/campaigns/${campaignId}/analytics`),
  // Audiences
  listAudiences: () => api.get('/marketing/campaigns/audiences'),
  getAudience: (id) => api.get(`/marketing/campaigns/audiences/${id}`),
  createAudience: (data) => api.post('/marketing/campaigns/audiences', data),
  updateAudience: (id, data) => api.put(`/marketing/campaigns/audiences/${id}`, data),
  deleteAudience: (id) => api.delete(`/marketing/campaigns/audiences/${id}`),
  previewAudience: (id) => api.post(`/marketing/campaigns/audiences/${id}/preview`),
  // Suppressions
  listSuppressions: () => api.get('/marketing/campaigns/suppressions'),
  addSuppression: (data) => api.post('/marketing/campaigns/suppressions', data),
  removeSuppression: (id) => api.delete(`/marketing/campaigns/suppressions/${id}`),
  // Email settings
  getEmailSettings: () => api.get('/marketing/campaigns/settings/email'),
  updateEmailSettings: (data) => api.put('/marketing/campaigns/settings/email', data),
  sendTestEmail: () => api.post('/marketing/campaigns/settings/email/test'),
};


// SMS & Conversations API
export const smsAPI = {
  send: (data) => api.post('/sms/send', data),
  preview: (data) => api.post('/sms/preview', data),
  templates: {
    list: (params) => api.get('/sms/templates', { params }),
    create: (data) => api.post('/sms/templates', data),
    update: (id, data) => api.put(`/sms/templates/${id}`, data),
    remove: (id) => api.delete(`/sms/templates/${id}`),
  },
};

export const conversationsAPI = {
  list: (params) => api.get('/comm/', { params }),
  messages: (conversationId) => api.get(`/comm/${conversationId}/messages`),
};

export const contactCenterAPI = {
  threads: (params) => api.get('/contact-center/threads', { params }),
  threadDetail: (phone) => api.get('/contact-center/thread', { params: { phone } }),
  assign: (phone, customerId) => api.post(`/contact-center/assign?phone=${encodeURIComponent(phone)}`, { customer_id: customerId }),
  createCustomer: (phone, displayName) => api.post(`/contact-center/create-customer?phone=${encodeURIComponent(phone)}`, { display_name: displayName }),
  sendSms: (phone, body) => api.post(`/contact-center/send-sms?phone=${encodeURIComponent(phone)}`, { body }),
  markRead: (phone) => api.post('/contact-center/mark-read', { phone }),
  poll: (since) => api.get('/contact-center/poll', { params: { since } }),
  tagCallOutcome: (callId, outcome) => api.post('/contact-center/call-outcome', { call_id: callId, outcome }),
  analytics: (params) => api.get('/contact-center/analytics', { params }),
  followUpQueue: (params) => api.get('/contact-center/follow-up-queue', { params }),
  updateFollowUp: (callId, data) => api.patch(`/contact-center/calls/${callId}/follow-up`, data),
};

export const phoneNumbersAPI = {
  list: () => api.get('/voice/numbers'),
  create: (data) => api.post('/voice/numbers', data),
  remove: (id) => api.delete(`/voice/numbers/${id}`),
  getToken: () => api.post('/voice/token'),
  getCallLog: (params) => api.get('/voice/call-log', { params }),
  callStart: (data) => api.post('/voice/call/start', data),
  callEnd: (data) => api.post('/voice/call/end', data),
  callHistory: (params) => api.get('/voice/call/history', { params }),
  lookupCustomer: (phone) => api.get('/voice/call/lookup-by-phone', { params: { phone } }),
  getOtwSettings: () => api.get('/voice/otw-autotext'),
  updateOtwSettings: (data) => api.put('/voice/otw-autotext', data),
  getMissedCallSettings: () => api.get('/voice/missed-call-autotext'),
  updateMissedCallSettings: (data) => api.put('/voice/missed-call-autotext', data),
  // Global Calls Inbox
  globalCalls: (params) => api.get('/voice/call/all', { params }),
  frequentContacts: (limit = 15) => api.get('/voice/call/frequent', { params: { limit } }),
  getRecordingUrl: (callLogId, download = false) => {
    const token = localStorage.getItem('access_token');
    const params = new URLSearchParams({ token });
    if (download) params.set('download', 'true');
    return `${API_BASE}/voice/call/${callLogId}/recording?${params.toString()}`;
  },
  assignCallToCustomer: (callLogId, customerId) =>
    api.post(`/voice/call/${callLogId}/assign-customer`, { customer_id: customerId }),
  createCustomerFromCall: (callLogId, displayName) =>
    api.post(`/voice/call/create-customer-from-call?call_log_id=${callLogId}&display_name=${encodeURIComponent(displayName)}`),
};



// ── Maintenance Plans ────────────────────────────────────────────────────────
export const maintenancePlansAPI = {
  list: (activeOnly = true) => api.get('/maintenance-plans/', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/maintenance-plans/', data),
  update: (id, data) => api.put(`/maintenance-plans/${id}`, data),
  deactivate: (id) => api.delete(`/maintenance-plans/${id}`),
  deletePlan: (id) => api.delete(`/maintenance-plans/${id}`),
  deletePlanWithEnrollments: (id, data) => api.post(`/maintenance-plans/${id}/delete`, data),
  planEnrollments: (planId) => api.get(`/maintenance-plans/${planId}/enrollments`),
  customerEnrollments: (customerId) => api.get(`/customers/${customerId}/maintenance-plans`),
  enrollCustomer: (customerId, data) => api.post(`/customers/${customerId}/maintenance-plans`, data),
  updateEnrollment: (enrollmentId, data) => api.patch(`/maintenance-plan-enrollments/${enrollmentId}`, data),
  allEnrollments: (params) => api.get('/maintenance-plan-enrollments/', { params }),
  triggerBilling: () => api.post('/maintenance-plan-enrollments/process-billing'),
  triggerService: () => api.post('/maintenance-plan-enrollments/process-service'),
};

// ── Phone System Add-on ──────────────────────────────────────────────────────
export const phoneSystemAPI = {
  addonStatus: () => api.get('/phone-system/addon-status'),
  enableAddon: () => api.post('/phone-system/addon-enable'),
  disableAddon: () => api.post('/phone-system/addon-disable'),
  billingConfig: () => api.get('/phone-system/billing-config'),
  billingSummary: () => api.get('/phone-system/billing-summary'),
  billingHistory: (limit = 20) => api.get('/phone-system/billing-history', { params: { limit } }),
  overageShadow: () => api.get('/phone-system/overage-shadow'),
  overageShadowHistory: (limit = 6) => api.get('/phone-system/overage-shadow/history', { params: { limit } }),
  numbers: () => api.get('/phone-system/numbers'),
  searchNumbers: (params) => api.get('/phone-system/search', { params }),
  purchaseNumber: (data) => api.post('/phone-system/purchase', data),
  setPrimary: (id) => api.put(`/phone-system/numbers/${id}/primary`),
  releaseNumber: (id) => api.delete(`/phone-system/numbers/${id}`),
  portRequests: () => api.get('/phone-system/port-requests'),
  portRequest: (id) => api.get(`/phone-system/port-requests/${id}`),
  createPortRequest: (data) => api.post('/phone-system/port-requests', data),
  updatePortRequest: (id, data) => api.put(`/phone-system/port-requests/${id}`, data),
  deletePortRequest: (id) => api.delete(`/phone-system/port-requests/${id}`),
  submitPortRequest: (id) => api.post(`/phone-system/port-requests/${id}/submit`),
  syncPortRequest: (id) => api.post(`/phone-system/port-requests/${id}/sync`),
  checkPortability: (data) => api.post('/phone-system/port-requests/check-portability', data),
  uploadPortBill: (id, formData) => api.post(`/phone-system/port-requests/${id}/upload-bill`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  uploadPortCSR: (id, formData) => api.post(`/phone-system/port-requests/${id}/upload-csr`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  removePortCSR: (id) => api.delete(`/phone-system/port-requests/${id}/csr`),
  usage: () => api.get('/phone-system/usage'),
};

export const communicationsAPI = {
  // Setup state
  setupState: () => api.get('/communications/setup-state'),
  markSetupComplete: (complete) => api.put('/communications/setup-complete', { complete }),
  selectTier: (tier, confirmDowngrade = false) => api.put('/communications/select-tier', { tier, confirm_downgrade: confirmDowngrade }),
  tiers: () => api.get('/communications/tiers'),
  lineSummary: () => api.get('/communications/line-summary'),
  releaseNumber: (numberId, confirm = false) => api.post(`/communications/release-number/${numberId}`, { confirm }),
  updateTracking: (numberId, data) => api.put(`/communications/numbers/${numberId}/tracking`, data),
  updateCnam: (numberId, data) => api.put(`/communications/numbers/${numberId}/cnam`, data),
  cnamDefaults: () => api.get('/communications/cnam-defaults'),

  // Phase 1 — Tenant-facing texting setup
  textingStatus: () => api.get('/communications/texting/status'),
  textingProvision: () => api.post('/communications/texting/provision'),
  // Phase 2 — A2P Brand + Campaign submission
  submitA2PBrand: () => api.post('/communications/texting/a2p/brand'),
  submitA2PCampaign: (body) => api.post('/communications/texting/a2p/campaign', body),
  // Audio Library
  listAudio: () => api.get('/communications/audio-library'),
  uploadAudio: (formData) => api.post('/communications/audio-library', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  renameAudio: (id, data) => api.put(`/communications/audio-library/${id}`, data),
  deleteAudio: (id, confirm = false) => api.delete(`/communications/audio-library/${id}`, { params: { confirm } }),
  audioFileUrl: (id) => `${api.defaults.baseURL}/communications/audio-library/${id}/file`,
  // Call flows
  callFlows: () => api.get('/communications/call-flows'),
  createCallFlow: (data) => api.post('/communications/call-flows', data),
  getCallFlow: (id) => api.get(`/communications/call-flows/${id}`),
  updateCallFlow: (id, data) => api.put(`/communications/call-flows/${id}`, data),
  deleteCallFlow: (id) => api.delete(`/communications/call-flows/${id}`),
  assignNumbers: (flowId, ids) => api.put(`/communications/call-flows/${flowId}/numbers`, { phone_number_ids: ids }),
  // Ring groups
  ringGroups: () => api.get('/communications/ring-groups'),
  createRingGroup: (data) => api.post('/communications/ring-groups', data),
  updateRingGroup: (id, data) => api.put(`/communications/ring-groups/${id}`, data),
  deleteRingGroup: (id) => api.delete(`/communications/ring-groups/${id}`),
  // Voice settings
  voiceSettings: () => api.get('/communications/voice-settings'),
  updateVoiceSettings: (data) => api.put('/communications/voice-settings', data),
  // SMS templates (for comms settings)
  smsTemplates: () => api.get('/communications/sms-templates'),
  // Trust Hub
  getTrustHub: () => api.get('/communications/trust-hub'),
  saveTrustHub: (data) => api.post('/communications/trust-hub', data),
  submitTrustHub: () => api.post('/communications/trust-hub/submit'),
  syncTrustHub: () => api.post('/communications/trust-hub/sync'),
};

export const websiteServicesAPI = {
  listJobs: () => api.get('/website-services/jobs'),
  createJob: () => api.post('/website-services/jobs'),
  getJob: (id) => api.get(`/website-services/jobs/${id}`),
  saveQuestionnaire: (id, data) => api.put(`/website-services/jobs/${id}/questionnaire`, data),
  selectPackage: (id, data) => api.put(`/website-services/jobs/${id}/package`, data),
  createCheckout: (id) => api.post(`/website-services/jobs/${id}/checkout`),
  confirmPayment: (id) => api.post(`/website-services/jobs/${id}/confirm-payment`),
  demoBuild: (id) => api.post(`/website-services/jobs/${id}/demo-build`),
  downloadUrl: (id) => `${api.defaults.baseURL}/website-services/jobs/${id}/download`,
  thumbnailUrl: (id) => `${api.defaults.baseURL}/website-services/jobs/${id}/thumbnail`,
  listRevisions: (jobId) => api.get(`/website-services/jobs/${jobId}/revisions`),
  createRevision: (jobId, data) => api.post(`/website-services/jobs/${jobId}/revisions`, data),
  revisionCheckout: (revisionId) => api.post(`/website-services/revisions/${revisionId}/checkout`),
  revisionConfirmPayment: (revisionId) => api.post(`/website-services/revisions/${revisionId}/confirm-payment`),
  startRevision: (revisionId) => api.post(`/website-services/revisions/${revisionId}/start`),
  getRevision: (revisionId) => api.get(`/website-services/revisions/${revisionId}`),
  downloadRevisionUrl: (revisionId) => `${api.defaults.baseURL}/website-services/revisions/${revisionId}/download`,
  uploadInspiration: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/website-services/upload-inspiration', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  inspirationUrl: (path) => `${api.defaults.baseURL}/website-services/inspiration/${path}`,
};

export const phoneAnalyticsAPI = {
  dashboard: (range = 'cycle') => api.get('/phone-analytics/dashboard', { params: { range } }),
  perNumber: (range = 'cycle') => api.get('/phone-analytics/per-number', { params: { range } }),
  trends: (range = '7d') => api.get('/phone-analytics/trends', { params: { range } }),
  bySource: (range = 'cycle') => api.get('/phone-analytics/by-source', { params: { range } }),
};

// ==================== Custom Forms V1 ====================
export const formsAPI = {
  // Templates
  listTemplates: (params = {}) => api.get('/forms/templates', { params }),
  getTemplate: (id) => api.get(`/forms/templates/${id}`),
  createTemplate: (data) => api.post('/forms/templates', data),
  updateTemplate: (id, data) => api.put(`/forms/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/forms/templates/${id}`),
  // Fields
  addField: (templateId, data) => api.post(`/forms/templates/${templateId}/fields`, data),
  updateField: (templateId, fieldId, data) => api.put(`/forms/templates/${templateId}/fields/${fieldId}`, data),
  deleteField: (templateId, fieldId) => api.delete(`/forms/templates/${templateId}/fields/${fieldId}`),
  reorderFields: (templateId, fieldIds) => api.put(`/forms/templates/${templateId}/fields/reorder`, { field_ids: fieldIds }),
  // Submissions
  listSubmissions: (params = {}) => api.get('/forms/submissions', { params }),
  getSubmission: (id) => api.get(`/forms/submissions/${id}`),
  createSubmission: (data) => api.post('/forms/submissions', data),
  updateSubmission: (id, data) => api.put(`/forms/submissions/${id}`, data),
  submitForm: (id) => api.post(`/forms/submissions/${id}/submit`),
  reopenForm: (id) => api.post(`/forms/submissions/${id}/reopen`),
  deleteSubmission: (id) => api.delete(`/forms/submissions/${id}`),
  // Customer Form Review & Signature V1
  sendForSignature: (id, data = {}) => api.post(`/forms/submissions/${id}/send-for-signature`, data),
  getSignatureAction: (id) => api.get(`/forms/submissions/${id}/signature-action`),
  cancelSignature: (id) => api.post(`/forms/submissions/${id}/cancel-signature`),
  // Phase 1b: File uploads on `file` fields. Phase 3: pass `params` with
  // { section_key, instance_index } to write into a repeatable instance.
  uploadSubmissionFile: (submissionId, fieldKey, file, params = null) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/forms/submissions/${submissionId}/files/${fieldKey}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: params || undefined,
    });
  },
  deleteSubmissionFile: (submissionId, fieldKey, fileId, params = null) =>
    api.delete(`/forms/submissions/${submissionId}/files/${fieldKey}/${fileId}`, { params: params || undefined }),
  // Phase 4: PDF generation. `generatePdf` creates a JobAttachment (or replaces
  // the existing one). `downloadPdf` streams the latest PDF for inline view.
  generatePdf: (submissionId) =>
    api.post(`/forms/submissions/${submissionId}/generate-pdf`),
  downloadPdfUrl: (submissionId) => {
    // Returns a fully-qualified URL with auth header inheritance happening
    // via the same axios instance — but for direct anchor downloads we need
    // the absolute path. Caller can use api.get(...) if streaming, or open
    // a new tab. Using axios for the actual fetch ensures Authorization is
    // attached.
    return `/forms/submissions/${submissionId}/pdf`;
  },
  downloadPdf: (submissionId) =>
    api.get(`/forms/submissions/${submissionId}/pdf`, { responseType: 'blob' }),
};

export const techAPI = {
  myDay: (date) => api.get('/tech/my-day', { params: { date } }),
  getEvent: (eventId) => api.get(`/tech/events/${eventId}`),
  getEventDetail: (eventId) => api.get(`/tech/events/${eventId}/detail`),
  updateStatus: (eventId, status) => api.put(`/tech/events/${eventId}/status`, { status }),
  addNote: (eventId, note, noteType = 'internal') => api.post(`/tech/events/${eventId}/notes`, { note, note_type: noteType }),
  getNotes: (eventId) => api.get(`/tech/events/${eventId}/notes`),
  deleteNote: (noteId) => api.delete(`/tech/notes/${noteId}`),
  getAttachments: (eventId) => api.get(`/tech/events/${eventId}/attachments`),
  uploadPhoto: (eventId, file, caption) => {
    const form = new FormData();
    form.append('file', file);
    if (caption) form.append('caption', caption);
    return api.post(`/tech/events/${eventId}/photos`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Phase 3: pass `opts` with { section_key, instance_index } to write into
  // a repeatable instance. Omit/null = top-level write (legacy behavior).
  updateChecklist: (eventId, submissionId, fieldKey, value, opts = {}) =>
    api.put(`/tech/events/${eventId}/checklist/${submissionId}`, {
      field_key: fieldKey,
      value,
      section_key: opts.section_key ?? null,
      instance_index: opts.instance_index === undefined || opts.instance_index === null
        ? null
        : opts.instance_index,
    }),
  getMaterials: (eventId) => api.get(`/tech/events/${eventId}/materials`),
  addMaterial: (eventId, data) => api.post(`/tech/events/${eventId}/materials`, data),
  deleteMaterial: (materialId) => api.delete(`/tech/materials/${materialId}`),
  searchInventory: (query) => api.get(`/tech/inventory/search`, { params: { q: query || '', limit: 20 } }),
  getCloseoutCheck: (eventId) => api.get(`/tech/events/${eventId}/closeout`),
  serveFile: (path) => api.get(`/tech/files/${path}`, { responseType: 'blob' }),
  // Payment collection
  collectPayment: (eventId, data) => api.post(`/tech/events/${eventId}/collect-payment`, data),
  markPaid: (eventId) => api.post(`/tech/events/${eventId}/mark-paid`),
  // Estimates
  getEstimates: (eventId) => api.get(`/tech/events/${eventId}/estimates`),
  getEstimateDetail: (estimateId) => api.get(`/tech/estimates/${estimateId}`),
  createEstimate: (eventId, data = {}) => api.post(`/tech/events/${eventId}/estimates/create`, data),
  updateEstimate: (estimateId, data) => api.put(`/tech/estimates/${estimateId}`, data),
  addEstimateItem: (estimateId, data) => api.post(`/tech/estimates/${estimateId}/items`, data),
  updateEstimateItem: (itemId, data) => api.put(`/tech/estimates/items/${itemId}`, data),
  deleteEstimateItem: (itemId) => api.delete(`/tech/estimates/items/${itemId}`),
  addEstimateOption: (estimateId, data) => api.post(`/tech/estimates/${estimateId}/options`, data),
  deleteEstimateOption: (optionId) => api.delete(`/tech/estimates/options/${optionId}`),
  getAvailableChecklists: (eventId) => api.get(`/tech/events/${eventId}/available-checklists`),
  attachChecklist: (eventId, templateId) => api.post(`/tech/events/${eventId}/attach-checklist`, { template_id: templateId }),
  
  // Price Book search (for invoice/estimate creation)
  searchPriceBook: (query, itemType) => {
    const params = new URLSearchParams({ q: query || '', limit: '20' });
    if (itemType) params.set('item_type', itemType);
    return api.get(`/tech/price-book/search?${params.toString()}`);
  },
  
  // Per-job invoice creation
  createInvoice: (eventId, data) => api.post(`/tech/events/${eventId}/invoices`, data),
  
  // Photo caption editing
  updatePhotoCaption: (eventId, attachmentId, caption) => 
    api.put(`/tech/events/${eventId}/photos/${attachmentId}`, { caption }),

  // Stripe card payment
  createPaymentIntent: (invoiceId, amount) =>
    api.post(`/tech/invoices/${invoiceId}/create-payment-intent`, { amount }),
  confirmCardPayment: (invoiceId, paymentIntentId) =>
    api.post(`/tech/invoices/${invoiceId}/confirm-payment`, { payment_intent_id: paymentIntentId }),
  // Send payment link via SMS
  sendPaymentLink: (invoiceId) =>
    api.post(`/tech/invoices/${invoiceId}/send-payment-link`),
};


// Project Jobs API
export const projectsAPI = {
  list: (params) => api.get('/projects/', { params }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects/', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  convertToProject: (jobId) => api.post(`/projects/${jobId}/convert-to-project`),
  // Budget
  getBudget: (id) => api.get(`/projects/${id}/budget`),
  createBudgetGroup: (id, data) => api.post(`/projects/${id}/budget/groups`, data),
  updateBudgetGroup: (id, gid, data) => api.put(`/projects/${id}/budget/groups/${gid}`, data),
  deleteBudgetGroup: (id, gid) => api.delete(`/projects/${id}/budget/groups/${gid}`),
  createBudgetItem: (id, data) => api.post(`/projects/${id}/budget/items`, data),
  updateBudgetItem: (id, iid, data) => api.put(`/projects/${id}/budget/items/${iid}`, data),
  deleteBudgetItem: (id, iid) => api.delete(`/projects/${id}/budget/items/${iid}`),
  // Tasks
  listTasks: (id, params) => api.get(`/projects/${id}/tasks`, { params }),
  createTask: (id, data) => api.post(`/projects/${id}/tasks`, data),
  updateTask: (id, tid, data) => api.put(`/projects/${id}/tasks/${tid}`, data),
  deleteTask: (id, tid) => api.delete(`/projects/${id}/tasks/${tid}`),
  listTaskComments: (id, tid) => api.get(`/projects/${id}/tasks/${tid}/comments`),
  addTaskComment: (id, tid, data) => api.post(`/projects/${id}/tasks/${tid}/comments`, data),
  // Daily Logs
  listDailyLogs: (id) => api.get(`/projects/${id}/daily-logs`),
  createDailyLog: (id, data) => api.post(`/projects/${id}/daily-logs`, data),
  updateDailyLog: (id, lid, data) => api.put(`/projects/${id}/daily-logs/${lid}`, data),
  deleteDailyLog: (id, lid) => api.delete(`/projects/${id}/daily-logs/${lid}`),
  // Files
  listFiles: (id, params) => api.get(`/projects/${id}/files`, { params }),
  uploadFile: (id, formData) => api.post(`/projects/${id}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteFile: (id, fid) => api.delete(`/projects/${id}/files/${fid}`),
  // Activity
  getActivity: (id, params) => api.get(`/projects/${id}/activity`, { params }),
  // Schedule
  listSchedule: (id) => api.get(`/projects/${id}/schedule/`),
  createScheduleItem: (id, data) => api.post(`/projects/${id}/schedule/`, data),
  updateScheduleItem: (id, itemId, data) => api.put(`/projects/${id}/schedule/${itemId}`, data),
  deleteScheduleItem: (id, itemId) => api.delete(`/projects/${id}/schedule/${itemId}`),
  // Change Orders
  listChangeOrders: (id) => api.get(`/projects/${id}/change-orders`),
  createChangeOrder: (id, data) => api.post(`/projects/${id}/change-orders`, data),
  updateChangeOrder: (id, coId, data) => api.put(`/projects/${id}/change-orders/${coId}`, data),
  deleteChangeOrder: (id, coId) => api.delete(`/projects/${id}/change-orders/${coId}`),
  // Invoices
  listInvoices: (id) => api.get(`/projects/${id}/invoices`),
  createInvoice: (id, data) => api.post(`/projects/${id}/invoices`, data),
  updateInvoice: (id, invId, data) => api.put(`/projects/${id}/invoices/${invId}`, data),
  deleteInvoice: (id, invId) => api.delete(`/projects/${id}/invoices/${invId}`),
  // Payments
  listPayments: (id) => api.get(`/projects/${id}/payments`),
  createPayment: (id, data) => api.post(`/projects/${id}/payments`, data),
  deletePayment: (id, payId) => api.delete(`/projects/${id}/payments/${payId}`),
  // Financial Report
  financialReport: (params = {}) => api.get('/projects/financial-report', { params }),
};

// Custom Fields API
export const customFieldsAPI = {
  // Definitions
  listDefinitions: () => api.get('/custom-fields/definitions'),
  createDefinition: (data) => api.post('/custom-fields/definitions', data),
  updateDefinition: (id, data) => api.put(`/custom-fields/definitions/${id}`, data),
  deleteDefinition: (id) => api.delete(`/custom-fields/definitions/${id}`),
  // Templates
  listTemplates: () => api.get('/custom-fields/templates'),
  createTemplate: (data) => api.post('/custom-fields/templates', data),
  updateTemplate: (id, data) => api.put(`/custom-fields/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/custom-fields/templates/${id}`),
  // Per-job
  getJobFields: (jobId) => api.get(`/custom-fields/jobs/${jobId}`),
  assignFields: (jobId, data) => api.post(`/custom-fields/jobs/${jobId}/assign`, data),
  unassignFields: (jobId, data) => api.post(`/custom-fields/jobs/${jobId}/unassign`, data),
  setValues: (jobId, data) => api.put(`/custom-fields/jobs/${jobId}/values`, data),
};

// Workflow Templates API
export const workflowsAPI = {
  list: (params) => api.get('/workflows/', { params }),
  get: (id) => api.get(`/workflows/${id}`),
  create: (data) => api.post('/workflows/', data),
  update: (id, data) => api.put(`/workflows/${id}`, data),
  delete: (id) => api.delete(`/workflows/${id}`),
  duplicate: (id, data) => api.post(`/workflows/${id}/duplicate`, data || {}),
  preview: (jobId, data) => api.post(`/workflows/preview/${jobId}`, data),
  apply: (jobId, data) => api.post(`/workflows/apply/${jobId}`, data),
};

// Job Costing Settings API
export const jobCostingAPI = {
  // Units
  listUnits: (params) => api.get('/job-costing/units', { params }),
  createUnit: (data) => api.post('/job-costing/units', data),
  updateUnit: (id, data) => api.put(`/job-costing/units/${id}`, data),
  // Cost Types
  listCostTypes: (params) => api.get('/job-costing/cost-types', { params }),
  createCostType: (data) => api.post('/job-costing/cost-types', data),
  updateCostType: (id, data) => api.put(`/job-costing/cost-types/${id}`, data),
  // Cost Codes
  listCostCodes: (params) => api.get('/job-costing/cost-codes', { params }),
  createCostCode: (data) => api.post('/job-costing/cost-codes', data),
  updateCostCode: (id, data) => api.put(`/job-costing/cost-codes/${id}`, data),
};

// Project Stages API
export const projectStagesAPI = {
  list: (params) => api.get('/project-stages/', { params }),
  create: (data) => api.post('/project-stages/', data),
  update: (id, data) => api.put(`/project-stages/${id}`, data),
  delete: (id, moveToId) => api.delete(`/project-stages/${id}`, { params: moveToId ? { move_to_stage_id: moveToId } : {} }),
  reorder: (stageIds) => api.post('/project-stages/reorder', { stage_ids: stageIds }),
};

export const projectBoardsAPI = {
  list: () => api.get('/project-boards'),
  create: (data) => api.post('/project-boards', data),
  update: (id, data) => api.put(`/project-boards/${id}`, data),
  updateCardLayout: (id, data) => api.put(`/project-boards/${id}/card-layout`, data),
  delete: (id) => api.delete(`/project-boards/${id}`),
};

export const webchatAPI = {
  getWidget: () => api.get('/webchat/widget'),
  updateWidget: (data) => api.put('/webchat/widget', data),
  regenerateKey: () => api.post('/webchat/widget/regenerate-key'),
  addDomain: (domain) => api.post('/webchat/widget/domains', { domain }),
  removeDomain: (domainId) => api.delete(`/webchat/widget/domains/${domainId}`),
  listSessions: (params) => api.get('/webchat/sessions', { params }),
  getSession: (id) => api.get(`/webchat/sessions/${id}`),
  replyToSession: (id, body) => api.post(`/webchat/sessions/${id}/reply`, { body }),
  closeSession: (id) => api.post(`/webchat/sessions/${id}/close`),
  goOnline: () => api.post('/webchat/presence/online'),
  goOffline: () => api.post('/webchat/presence/offline'),
  getPresence: () => api.get('/webchat/presence'),
  getStats: () => api.get('/webchat/stats'),
  getAnalytics: (days) => api.get('/webchat/analytics', { params: { days } }),
  createLead: (sessionId, stageId) => api.post(`/webchat/sessions/${sessionId}/create-lead`, { stage_id: stageId || null }),
  assignPipeline: (sessionId, stageId) => api.post(`/webchat/sessions/${sessionId}/assign-pipeline`, { stage_id: stageId }),
  getPipelineStages: () => api.get('/webchat/pipeline-stages'),
  getNotifications: (since) => api.get('/webchat/notifications', { params: { since } }),
};



export const employeeClockAPI = {
  status: () => api.get('/employee-clock/status'),
  clockIn: (data) => api.post('/employee-clock/clock-in', data || {}),
  clockOut: (data) => api.post('/employee-clock/clock-out', data),
  history: (days) => api.get('/employee-clock/history', { params: { days } }),
};
